import { useEffect, useMemo, useRef, useState } from 'react'
import {
  calcBeleg,
  rundeEndtotal,
  lineTotal,
  percentOf,
  type SessionUser
} from '@raporto/shared'
import { OfficeHead } from '../ui/OfficeShell.js'
import { Datumsfeld } from '../ui/Datumsfeld.js'
import { MailEditor, type Mailentwurf } from '../ui/MailEditor.js'
import { ausFehler, springeZu, EXPRESS_FELDER } from '../ui/Feldfehler.js'
import { useEntwurfsSicherung } from '../ui/useEntwurfsSicherung.js'
import { notify, notifyError } from '../ui/Toasts.js'
import { useCacheTable } from '../ui/hooks.js'
import { OrtsFeldPaar, StrassenFeld, useStrassen } from '../ui/OrtsFelder.js'
import { Betragsfeld, Zahlenfeld } from '../ui/Zahlenfeld.js'
import {
  KundeAnlegen,
  LeistungAnlegen,
  ProjektAnlegen,
  type NeueLeistung
} from '../ui/SchnellAnlage.js'
import { eigeneOrtschaften } from '../ortschaften.js'
import { db } from '../net/localDb.js'
import {
  api,
  type TemplateSummary,
  type DocumentSummary,
  type Versandwege
} from '../net/apiClient.js'
import { uuidv7 } from '../net/ids.js'
import { PdfAnsicht } from '../ui/PdfAnsicht.js'
import { ActionButton } from '../ui/ActionButton.js'

/**
 * Express-Rechnung – der Alltagsweg des Büros.
 *
 * Bisher führte der einzige Weg zu einer Rechnung über einen Rapport. Das ist
 * richtig für Arbeit, die auf einer Baustelle passiert, und falsch für alles
 * andere: eine Materiallieferung, eine Kleinreparatur, eine Akontozahlung.
 * Dafür gibt es nichts zu rapportieren, und trotzdem muss eine Rechnung
 * hinaus.
 *
 * Der Aufbau folgt der Vorlage, weil der Betrieb ihn kennt: oben Kunde und
 * Objekt, in der Mitte die Positionen, unten die Optionen und die
 * Aktionsleiste.
 *
 * Gearbeitet wird zuerst lokal. Erst beim Speichern entsteht der Beleg auf dem
 * Server – so entstehen keine halben Entwürfe, nur weil jemand den Bildschirm
 * geöffnet und wieder verlassen hat.
 */

interface Zeile {
  key: string
  description: string
  quantity: number
  unit: string
  unitPriceRp: number
  vatRate: number
  discountPercent: number
  kind: 'arbeit' | 'material'
}

interface Kunde {
  id: string
  name: string
  street?: string
  zip?: string
  city?: string
}

/**
 * Eine Baustelle, so wie sie im lokalen Zwischenspeicher liegt.
 *
 * Gesucht wird darin und nicht über die Schnittstelle: die Baustellen sind
 * beim Abgleich ohnehin schon da, und eine Suche, die auf das Netz wartet,
 * ist im Büro mit dreihundert Baustellen spürbar langsamer als eine, die es
 * nicht tut.
 */
interface Baustelle {
  id: string
  customerId: string
  name: string
  street?: string | null
  zip?: string | null
  city?: string | null
  status?: string | null
  archivedAt?: string | null
  deletedAt?: string | null
}

interface KatalogEintrag {
  id: string
  code: string
  name: string
  unit: string
  unitPriceRp: number
  vatRate: number
  kind: 'arbeit' | 'material'
  active?: boolean
}

const rp = (n: number): string =>
  (n / 100).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** «8.1» und «0», nicht «8.10» und «0.0». Der Satz steht so auch auf dem Beleg. */
const satzText = (n: number): string => String(Math.round(n * 100) / 100)

const heute = (): string => new Date().toISOString().slice(0, 10)

/** Ein ISO-Tag plus n Tage, wieder als ISO-Tag. */
function plusTage(iso: string, tage: number): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return ''
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + tage)
  return d.toISOString().slice(0, 10)
}

/** Wie viele Tage liegen zwischen zwei ISO-Tagen? */
function tageDazwischen(von: string, bis: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(von) || !/^\d{4}-\d{2}-\d{2}$/.test(bis)) return null
  const a = Date.parse(`${von}T00:00:00Z`)
  const b = Date.parse(`${bis}T00:00:00Z`)
  return Math.round((b - a) / 86_400_000)
}

const alsDatum = (iso: string): string =>
  /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T00:00:00`).toLocaleDateString('de-CH') : '–'

/**
 * Eine leere Zeile ist leer.
 *
 * Vorher standen Menge 1 und Einheit m² drin. Gut gemeint, in der Praxis
 * lästig: wer 100 m² erfasst, muss die 1 erst wegputzen, und wer Stunden
 * schreibt, überschreibt ein m², das nie jemand wollte. Ein Vorschlag, den man
 * löschen muss, ist kein Vorschlag.
 */
function leereZeile(): Zeile {
  return {
    key: uuidv7(),
    description: '',
    quantity: 0,
    unit: '',
    unitPriceRp: 0,
    vatRate: 8.1,
    discountPercent: 0,
    kind: 'arbeit'
  }
}

/**
 * Rechnung oder Offerte – ein Bildschirm, ein Schalter.
 *
 * Die beiden unterscheiden sich in vier Dingen: Titel, Gültigkeit statt
 * Zahlungsfrist, eigener Nummernkreis und kein Zahlteil auf dem Papier. Alles
 * andere – Kunde, Objekt, Positionen, Katalogsuche, Rabatt, nächster Schritt,
 * Vorlagen – ist identisch.
 *
 * Zwei Bildschirme hiessen: jede Verbesserung an der Positionstabelle zweimal
 * machen, und beim zweiten Mal vergessen. Genau so entstehen zwei Formulare,
 * von denen eines seit Monaten einen Fehler hat, den niemand meldet, weil alle
 * das andere benutzen.
 */
export function ExpressInvoice({
  session,
  aufBeleg,
  art = 'rechnung',
  vorgabe,
  onVorgabeUebernommen,
  aufEinstellungen
}: {
  session: SessionUser
  aufBeleg?: (id: string) => void
  art?: 'rechnung' | 'offerte'
  /**
   * Ein Schnellstart aus einer Liste: Kunde und Baustelle stehen schon fest.
   *
   * Der häufigste Weg im Büro ist «diesen Kunden habe ich gerade am Telefon».
   * Ohne diese Vorgabe müsste er den Namen ein zweites Mal suchen – und zwar
   * in einer Auswahlliste mit dreihundert Einträgen, direkt nachdem er ihn
   * angeklickt hat.
   */
  vorgabe?: {
    customerId: string
    projectId?: string
    projectName?: string
    objectStreet?: string | null
    objectZip?: string | null
    objectCity?: string | null
  } | null
  onVorgabeUebernommen?: () => void
  /**
   * Der Weg zu den Einstellungen.
   *
   * Gebraucht wird er an genau einer Stelle: wenn die Handelsregister-Suche
   * noch nicht eingerichtet ist und jemand wissen will, wie das geht. Ein
   * Hinweis, der auf eine Seite zeigt, zu der er nicht führt, ist eine
   * Sackgasse mit Anleitung.
   */
  aufEinstellungen?: () => void
}): JSX.Element {
  const istOfferte = art === 'offerte'
  const wort = istOfferte ? 'Offerte' : 'Rechnung'
  /*
    Über useCacheTable und nicht einmalig beim Öffnen gelesen.

    Der Abgleich kann noch laufen, wenn dieser Bildschirm erscheint – direkt
    nach dem Anmelden ist das der Normalfall. Ein einmaliges Lesen zeigte
    dann eine leere Kundenliste, und zwar dauerhaft: der Bildschirm hätte nie
    erfahren, dass die Daten inzwischen da sind.
  */
  const { rows: kundenRoh } = useCacheTable('customers')
  const { rows: katalogRoh } = useCacheTable('catalogItems')
  const { rows: projekteRoh } = useCacheTable('projects')
  const [mwst, setMwst] = useState(8.1)
  /* Das PDF bleibt in der App – siehe ui/PdfAnsicht.tsx. */
  const [pdfAnsicht, setPdfAnsicht] = useState<{ url: string; titel: string } | null>(null)
  const [frist, setFrist] = useState(30)
  const [gueltigTage, setGueltigTage] = useState(30)

  const [kundeId, setKundeId] = useState('')
  /*
    Die Baustelle als Datensatz, nicht nur als Text.

    Getippt wird sie meistens – eine Express-Rechnung braucht kein Projekt.
    Kommt sie aber aus dem Baustellenblatt, ist sie ein echter Datensatz, und
    dann soll der Beleg daran hängen: sonst steht er nachher nirgends, wo
    jemand ihn sucht.
  */
  const [projektId, setProjektId] = useState<string | null>(null)
  const [projektName, setProjektName] = useState('')
  /*
    Was im Suchfeld steht – getrennt vom gewählten Datensatz.

    Beides in einem Feld zu führen war der Fehler: jeder Tastendruck löschte
    die Verknüpfung, und deshalb liess sich eine bestehende Baustelle
    überhaupt nicht anhängen. Jetzt ist die Auswahl ein Zustand für sich; das
    Suchfeld darf getippt werden, ohne sie umzuwerfen.
  */
  const [projektSuche, setProjektSuche] = useState('')
  const [strasse, setStrasse] = useState('')
  const [plz, setPlz] = useState('')
  const [ort, setOrt] = useState('')
  const [von, setVon] = useState('')
  const [bis, setBis] = useState('')
  const [rabatt, setRabatt] = useState(0)
  const [bemerkung, setBemerkung] = useState('')
  /*
    Optionen offen, nicht zugeklappt.

    Zugeklappt hat niemand gesehen, dass es Rabatt, Frist und einen nächsten
    Schritt überhaupt gibt – und wer sie nicht sieht, füllt sie nicht aus.
  */
  const [optionenOffen, setOptionenOffen] = useState(true)

  /*
    Das Belegdatum und das, was daraus folgt.

    «zahlbar bis» und «gültig bis» rechnet niemand gern im Kopf, und niemand
    tippt sie gern jedes Mal. Also stehen sie da, sobald der Bildschirm
    aufgeht, und wandern mit, wenn das Belegdatum sich ändert.

    Bis jemand sie von Hand anfasst. Dann bleibt stehen, was er geschrieben
    hat – ein Datum, das der Anwender gesetzt hat und die Software gleich
    wieder überschreibt, ist schlimmer als gar keine Vorgabe.
  */
  const [datum, setDatum] = useState(heute())
  /* Leer heisst: gerechnet. Gefüllt heisst: jemand hat es hingeschrieben. */
  const [zielManuell, setZielManuell] = useState('')
  const [gueltigBis, setGueltigBis] = useState('')
  const [aufgabe, setAufgabe] = useState('')
  const [aufgabeDatum, setAufgabeDatum] = useState('')
  const [aufgabeNotiz, setAufgabeNotiz] = useState('')

  const [zeilen, setZeilen] = useState<Zeile[]>([leereZeile()])
  const [suche, setSuche] = useState('')

  /*
    Welche Schnellanlage offen ist – höchstens eine.

    Drei unabhängige Schalter hätten irgendwann zwei Masken gleichzeitig
    aufgeklappt, und der Beleg darunter wäre nicht mehr zu sehen gewesen.
  */
  const [anlage, setAnlage] = useState<'kunde' | 'projekt' | 'leistung' | null>(null)

  const [belegId, setBelegId] = useState<string | null>(null)
  /*
    Die Version des Serverstands.

    Sie geht bei jedem automatischen Speichern mit und ist der Konfliktschutz:
    Wer nicht auf dem aktuellen Stand ist, bekommt eine Meldung, statt die
    Arbeit eines zweiten Geräts stillschweigend zu überschreiben.
  */
  const [belegVersion, setBelegVersion] = useState<number | null>(null)
  /*
    Dieselbe Version als Referenz – für den laufenden Durchgang.

    Ein Durchgang von `speichern()` schreibt zweimal: Kopf, dann Positionen.
    Der Zustand aus `useState` trägt zwischen diesen beiden Zeilen noch den
    ALTEN Wert, weil React erst nach dem Durchlauf neu zeichnet. Genau dieser
    eine Schritt Verzug war der gemeldete Versionskonflikt. Die Referenz ist
    sofort aktuell; der Zustand bleibt daneben für die Anzeige und für den
    Sicherungshaken.
  */
  const versionJetzt = useRef<number | null>(null)
  /*
    Ein Speichervorgang je Beleg, und zwar wirklich einer.

    Gesichert wird aus drei Richtungen: der Zeitgeber der automatischen
    Sicherung, «Entwurf speichern» und «Abschliessen». Liefen zwei davon
    gleichzeitig, schrieben beide dieselben Positionen mit derselben
    Basisversion – einer gewann, der andere bekam den Konflikt, und welcher,
    entschied das Netz.

    Dieses Versprechen reicht nicht aus dem Sicherungshaken heraus: Der kennt
    seinen eigenen Lauf, nicht den Knopf. Deshalb hängt die Kette hier, an der
    Maske, durch die alle drei gehen. Sie reiht, sie verwirft nicht – was
    wartet, läuft danach mit dem dann aktuellen Formularstand.
  */
  const kette = useRef<Promise<unknown>>(Promise.resolve())
  /*
    Der E-Mail-Editor – dieselbe Regel wie im Belegdetail: Keine Nachricht
    verlässt das Haus, ohne dass jemand sie gesehen hat.
  */
  const [mail, setMail] = useState<Mailentwurf | null>(null)
  /*
    Was an welchem Feld fehlt.

    Der Server schickt die Punkte als Liste mit Feldnamen; bisher warf der
    Client den Feldnamen weg und zeigte einen Sammelsatz als Toast. Hier
    bleibt er erhalten – `amFeld` trägt den roten Rahmen, `ohneFeld` die
    Punkte, die in den Einstellungen stehen und nicht in dieser Maske.
  */
  const [fehlerAmFeld, setFehlerAmFeld] = useState<Record<string, string>>({})
  const [fehlerOhneFeld, setFehlerOhneFeld] = useState<string[]>([])
  const [nummer, setNummer] = useState<string | null>(null)
  /*
    Nummer und PDF sind zwei Dinge, und sie gehen getrennt schief.

    Der Abschluss läuft in drei Schritten: die Nummer wird in einer
    Transaktion vergeben, danach – ausserhalb – wird das PDF gedruckt, und
    erst dann gilt der Beleg als bereit. Scheitert der Druck, hat der Beleg
    eine Nummer und kein PDF. Genau das meldete dieser Bildschirm bis hierher
    als «abgeschlossen» und bot darunter «PDF öffnen» an, das ins Leere
    zeigte. Aus Produktion gemeldet als «Knopf reagiert nicht sauber».
  */
  const [pdfDa, setPdfDa] = useState(false)
  /*
    Abgeschlossen heisst: fertig, nicht «fast fertig».

    Sobald eine Nummer vergeben ist, ist der Beleg ein Dokument – er hat eine
    Nummer aus dem Nummernkreis, ein PDF liegt in der Ablage, und beim Kunden
    liegt womöglich schon ein Ausdruck. Der Server weist jede Änderung daran
    mit 409 ab (`ladeEntwurf` in routes/documents.ts), und zwar an allen neun
    Wegen – nachgemessen in proben/abschluss-sperre-probe.mjs.

    Die Oberfläche hielt sich nicht daran: Felder, Mengen, Rabatt und
    Optionen blieben eingabebereit. Wer dort tippte, sah das Total sich
    ändern und speicherte nichts – es gab nach dem Abschluss gar keinen
    Speicherknopf mehr. Also kein Datenverlust in der Datenbank, aber die
    schlechtere Sorte Fehler: eine Anwendung, die etwas anzubieten scheint,
    was sie nicht tut.
  */

  /*
    Was mit diesem Beleg noch geht – erst nach dem Abschliessen geladen.

    Vorher endete dieser Bildschirm nach dem Abschluss bei «PDF öffnen» und
    sonst nichts. Der nächste Schritt (senden, bezahlen) stand nur im
    Belegdetail, das man über Menü und Liste neu suchen musste: gemessen zwei
    Klicks und ein Ortswechsel, jedes Mal.

    Geladen wird erst, wenn es eine Nummer gibt. Vorher gäbe es nichts zu
    fragen, und die Antwort kostet einen Serveraufruf.
  */
  const [wege, setWege] = useState<Versandwege | null>(null)

  /*
    ════════════════════════════════════════════════════════════════════════
    Gesperrt wird beim VERSAND, nicht bei der Nummer
    ════════════════════════════════════════════════════════════════════════

    Hier stand `Boolean(nummer)`, und damit war die Maske in dem Moment dicht,
    in dem der Server eine Nummer vergab – also bevor der Beleg irgendwo
    gewesen war. Wer sich in der Menge vertippt hatte, musste stornieren und
    von vorne beginnen; jedes Mal verbrannte das eine Nummer.

    Massgeblich ist jetzt dieselbe Frage wie auf dem Server: Ist der Beleg
    draussen? Die Antwort kommt aus den Versandwegen – drei Zeitstempel und
    der Vermerk von Hand. Solange keiner gesetzt ist, bleibt alles änderbar,
    und die Nummer bleibt dieselbe.

    Solange die Versandwege noch nicht geladen sind (`wege === null`), gilt
    der Beleg mit Nummer vorsichtshalber als gesperrt. Lieber eine Sekunde zu
    lange gesperrt als ein Feld, das sich ändern lässt und beim Speichern
    einen Fehler wirft.
  */
  const versendet = wege
    ? Boolean(
        wege.gesendet.anKunde ||
          wege.gesendet.anFirma ||
          wege.gesendet.anBenutzer ||
          wege.vermerk?.am
      )
    : Boolean(nummer)
  const abgeschlossen = versendet
  const [busy, setBusy] = useState<string | null>(null)

  /*
    Die drei Startwege.

    Sie füllen alle dasselbe Formular und verschwinden dann aus dem Vorgang.
    Danach ist nicht mehr unterscheidbar, ob jemand aus einer Vorlage gestartet
    hat oder von Hand getippt – und das ist richtig so: die Rechnung gehört dem
    Betrieb, nicht der Vorlage.
  */
  const [wahl, setWahl] = useState<'vorlage' | 'letzte' | 'duplikat' | null>(null)
  const [vorlagen, setVorlagen] = useState<TemplateSummary[]>([])
  const [belege, setBelege] = useState<DocumentSummary[]>([])
  const [herkunft, setHerkunft] = useState<string | null>(null)
  const [vorlageName, setVorlageName] = useState('')

  const kunden = useMemo(
    () =>
      (kundenRoh as unknown as Array<Kunde & { active?: boolean }>)
        .filter((k) => k.active !== false)
        .sort((a, b) => String(a.name).localeCompare(String(b.name), 'de-CH')),
    [kundenRoh]
  )
  const katalog = useMemo(
    () => (katalogRoh as unknown as KatalogEintrag[]).filter((c) => c.active !== false),
    [katalogRoh]
  )

  /*
    Die Baustellen, unter denen gesucht wird.

    Gelöschte fallen weg – an einer gelöschten Baustelle soll kein neuer Beleg
    mehr hängen. Archivierte bleiben: eine Nachtragsrechnung zu einem
    abgeschlossenen Auftrag ist der Normalfall und nicht die Ausnahme.
  */
  const baustellen = useMemo(
    () =>
      (projekteRoh as unknown as Baustelle[]).filter(
        (p) => p && p.id && !p.deletedAt
      ),
    [projekteRoh]
  )

  /*
    Die Ortschaften kommen jetzt aus einer Stelle für die ganze Anwendung –
    eigener Bestand zuerst, amtliches Verzeichnis für alles, was der Betrieb
    noch nie erfasst hat. Siehe ui/OrtsFelder.tsx.
  */
  const orte = useMemo(
    () =>
      eigeneOrtschaften(
        kundenRoh as unknown as Array<Record<string, unknown>>,
        projekteRoh as unknown as Array<Record<string, unknown>>
      ).sort((a, b) => a[0].localeCompare(b[0])),
    [kundenRoh, projekteRoh]
  )
  const strassen = useStrassen()

  /*
    Die Baustellensuche.

    Gesucht wird über alles, womit jemand eine Baustelle im Kopf hat:
    Bezeichnung, Strasse, PLZ, Ort und der Name des Kunden. Wer «Bahnhof»
    tippt, findet die Baustelle an der Bahnhofstrasse; wer «Meier» tippt,
    findet die Baustellen von Meier.

    Erst ab zwei Zeichen, und nur beim Tippen – nicht beim Hineinklicken.
    Eine Liste, die beim Fokus aufgeht und dreihundert Einträge zeigt, ist
    keine Hilfe, sondern eine Wand.

    Ist ein Kunde gewählt, gehören nur seine Baustellen dazu. Eine Baustelle
    hat genau einen Kunden; die Rechnung an Meier an die Baustelle von Müller
    zu hängen, ergäbe einen Beleg, der im falschen Baustellenblatt auftaucht.
  */
  const projektTreffer = useMemo(() => {
    const q = projektSuche.trim().toLowerCase()
    if (q.length < 2) return []
    const woerter = q.split(/\s+/).filter(Boolean)
    const kandidaten = kundeId ? baustellen.filter((p) => p.customerId === kundeId) : baustellen
    return kandidaten
      .map((p) => {
        const kunde = kunden.find((k) => k.id === p.customerId)
        const heuhaufen = [p.name, p.street, p.zip, p.city, kunde?.name]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        return { projekt: p, kunde, heuhaufen }
      })
      .filter((t) => woerter.every((w) => t.heuhaufen.includes(w)))
      .sort((a, b) => a.projekt.name.localeCompare(b.projekt.name, 'de-CH'))
      .slice(0, 8)
  }, [projektSuche, baustellen, kunden, kundeId])

  const gewaehlteBaustelle = useMemo(
    () => (projektId ? (baustellen.find((p) => p.id === projektId) ?? null) : null),
    [projektId, baustellen]
  )

  /**
   * Eine Baustelle anhängen.
   *
   * Die Objektadresse wird nur übernommen, wenn im Formular noch keine steht.
   * Wer sie schon getippt hat, hat sich etwas dabei gedacht – sie ihm unter
   * den Fingern wegzuschreiben, ist der sichere Weg zur falschen Adresse auf
   * dem Papier. Für den anderen Fall gibt es den Knopf daneben.
   */
  function waehleBaustelle(p: Baustelle): void {
    setProjektId(p.id)
    setProjektName(p.name)
    setProjektSuche('')
    if (!kundeId) setKundeId(p.customerId)
    if (!strasse && !plz && !ort) {
      setStrasse(p.street ?? '')
      setPlz(p.zip ?? '')
      setOrt(p.city ?? '')
    }
  }

  function loeseBaustelle(): void {
    setProjektId(null)
    setProjektSuche('')
  }

  /*
    Die Versandwege nachladen, sobald der Beleg eine Nummer hat.

    Nur dann: vorher gibt es keinen Beleg, den man senden könnte. Schlägt der
    Aufruf fehl, bleibt `wege` leer – die Aktionsleiste zeigt dann PDF und
    den Weg zum Beleg, und der Versand steht im Beleg wie bisher. Ein
    fehlgeschlagener Nebenaufruf darf den abgeschlossenen Beleg nicht
    verdecken.
  */
  useEffect(() => {
    if (!belegId || !nummer) { setWege(null); return }
    let abgebrochen = false
    void api
      .versandwege(belegId)
      .then((w) => { if (!abgebrochen) setWege(w) })
      .catch(() => { if (!abgebrochen) setWege(null) })
    return () => { abgebrochen = true }
  }, [belegId, nummer])

  useEffect(() => {
    void db.settings.get('tenant').then((s) => {
      if (!s) return
      const r = s as Record<string, unknown>
      setMwst(Number(r.defaultVatRate ?? 8.1))
      setFrist(Number(r.paymentTermDays ?? 30))
      setGueltigTage(Number(r.quoteValidDays ?? 30))
    })
  }, [])

  /*
    Das Zieldatum: bei der Rechnung «zahlbar bis», bei der Offerte «gültig bis».

    Gerechnet und nicht gespeichert, solange niemand es angefasst hat. Bei der
    Rechnung führt es zurück auf die Zahlungsfrist – der Server kennt nur die
    Tage und rechnet das Datum beim Abschliessen daraus. So gibt es auch hier
    nur eine Wahrheit und nicht ein Datum neben einer Frist, die sich
    widersprechen können.
  */
  const zielDatum = zielManuell || plusTage(datum, istOfferte ? gueltigTage : frist)
  const zielSelbstGesetzt = zielManuell !== ''

  /*
    Nachfassen einer Offerte: zur Hälfte der Gültigkeit.

    Regel des CEO: 30 Tage gültig → nach 15 Tagen nachfassen, aufgerundet bei
    ungerader Dauer. Vorher stand im «Fällig am» nichts, und der Kalender ging
    beim heutigen Tag auf – nachgefasst wurde damit am Tag des Versands.

    Nur ein Vorschlag, und nur bei der Offerte: Wer das Datum selbst setzt,
    behält es; die Rechnung und ihre Zahlungsfrist bleiben davon unberührt.
    Gespeichert wird es, wie bisher, nur zusammen mit einer Aufgabe.
  */
  const gueltigkeitTage = tageDazwischen(datum, zielDatum) ?? gueltigTage
  const nachfassVorschlag =
    istOfferte && gueltigkeitTage > 0 ? plusTage(datum, Math.ceil(gueltigkeitTage / 2)) : ''
  const aufgabeDatumWirksam = aufgabeDatum || nachfassVorschlag

  function setzeZielDatum(wert: string): void {
    setZielManuell(wert)
    if (istOfferte) {
      setGueltigBis(wert)
      return
    }
    /*
      Rechnung: aus dem Datum wird die Frist zurückgerechnet.

      Der Server kennt nur Tage und rechnet die Fälligkeit beim Abschliessen
      daraus. Gäbe es hier ein Datum UND dort eine Frist, hätten wir zwei
      Wahrheiten, die beim ersten Ändern auseinanderlaufen.
    */
    const tage = tageDazwischen(datum, wert)
    if (tage !== null && tage >= 0) setFrist(tage)
  }

  const kunde = kunden.find((k) => k.id === kundeId) ?? null

  const treffer = useMemo(() => {
    const q = suche.trim().toLowerCase()
    if (!q) return []
    return katalog.filter((c) => `${c.code} ${c.name}`.toLowerCase().includes(q)).slice(0, 8)
  }, [suche, katalog])

  /*
    Was zählt, ist die Bezeichnung – nicht die Menge.

    Bisher fiel jede Zeile mit Menge 0 lautlos heraus. Gemeint war die leere
    Zeile am Ende des Formulars; getroffen wurde auch die Zeile, die jemand
    beschriftet und deren Menge er noch nicht gesetzt hat. Sie verschwand
    beim Speichern, ohne dass irgendwo stand, dass sie verschwindet – und
    wer «Beratungsgespräch inklusive» zu 0.00 eintrug, fand es hinterher
    nicht auf dem Beleg.

    Die leere Zeile fängt die Bezeichnung ab: ohne Text keine Position. Alles
    mit Text bleibt stehen, und wenn die Menge fehlt, sagt es der Abschluss
    – benannt und mit Bezeichnung, statt lautlos.
  */
  const echteZeilen = zeilen.filter((z) => z.description.trim().length > 0)

  /*
    Gerechnet wird mit derselben Funktion wie auf dem Server.

    Hier stand eine eigene Rechnung: MWST je Position ermitteln, aufsummieren
    und den Steuerbetrag anschliessend im Verhältnis des Rabatts kürzen. Der
    Server kürzt stattdessen die Bemessungsgrundlage und rechnet die Steuer
    daraus neu – das ist, was der Treuhänder nachrechnet. Beides ergibt meist
    denselben Betrag und gelegentlich einen Rappen Unterschied, und dann zeigt
    der Bildschirm eine andere Zahl als das Papier.

    Ein Rappen ist kein Geld, aber zwei Zahlen für denselben Beleg sind ein
    Fehler. Also dieselbe Funktion, aus `@raporto/shared`.
  */
  const summe = useMemo(() => {
    /*
      Gerundet wie auf dem Server.

      Das Formular zeigt denselben Betrag, den der Beleg tragen wird – sonst
      springt das Total beim Speichern um zwei Rappen, und niemand weiss,
      welche Zahl gilt. Dieselbe Funktion wie im Server, aus `@raporto/shared`.
    */
    const s = rundeEndtotal(
      calcBeleg(
        echteZeilen.map((z) => ({
          quantity: z.quantity,
          unitPriceRp: z.unitPriceRp,
          vatRate: z.vatRate,
          discountPercent: z.discountPercent
        })),
        rabatt
      )
    )
    return {
      brutto: s.zwischentotalRp,
      rabattRp: s.rabattRp,
      netto: s.subtotalRp,
      steuer: s.vatTotalRp,
      rundungRp: s.rundungRp,
      /*
        Eine Zeile je Satz, nicht eine Mischzahl. Zwei Sätze auf einem Beleg
        sind im Handwerk normal – Arbeit zum Normalsatz, eine Position zum
        reduzierten – und wer die Summe prüft, will sehen, welcher Satz auf
        welche Grundlage gerechnet wurde.
      */
      saetze: s.vatBySatz
        .filter((v) => v.vatRp !== 0)
        .sort((a, b) => b.rate - a.rate)
        .map((v) => ({ satz: v.rate, betragRp: v.vatRp })),
      total: s.totalRp
    }
  }, [echteZeilen, rabatt])

  function setzeZeile(key: string, teil: Partial<Zeile>): void {
    setZeilen((alt) => alt.map((z) => (z.key === key ? { ...z, ...teil } : z)))
  }

  function katalogHinzu(c: KatalogEintrag): void {
    setZeilen((alt) => [
      ...alt.filter((z) => z.description.trim() || z.unitPriceRp > 0),
      {
        key: uuidv7(),
        description: c.name,
        quantity: 1,
        unit: c.unit,
        unitPriceRp: c.unitPriceRp,
        vatRate: Number(c.vatRate) || mwst,
        discountPercent: 0,
        kind: c.kind
      }
    ])
    setSuche('')
  }

  /*
    Die Vorgabe füllt das Formular genau einmal und verschwindet dann.

    Sie bleibt nicht als Zustand liegen: sonst überschriebe sie beim nächsten
    Zeichnen, was der Benutzer inzwischen selbst eingetippt hat.
  */
  useEffect(() => {
    if (!vorgabe) return
    setKundeId(vorgabe.customerId)
    setProjektId(vorgabe.projectId ?? null)
    if (vorgabe.projectName) setProjektName(vorgabe.projectName)
    if (vorgabe.objectStreet) setStrasse(vorgabe.objectStreet)
    if (vorgabe.objectZip) setPlz(vorgabe.objectZip)
    if (vorgabe.objectCity) setOrt(vorgabe.objectCity)
    onVorgabeUebernommen?.()
  }, [vorgabe, onVorgabeUebernommen])

  function adresseVomKunden(): void {
    if (!kunde) return
    setStrasse(kunde.street ?? '')
    setPlz(kunde.zip ?? '')
    setOrt(kunde.city ?? '')
  }

  // -------------------------------------------------------------------------
  // Vier Wege zu einer Rechnung
  // -------------------------------------------------------------------------

  /**
   * Zurück auf leer.
   *
   * Bewusst kein Neuladen der Seite: das hier ist derselbe Bildschirm, und ein
   * Neuladen würde bei langsamer Verbindung eine Sekunde weisse Fläche zeigen –
   * für etwas, das ein paar Zuweisungen sind.
   *
   * Der Kunde bleibt absichtlich NICHT stehen. Wer „Neue Rechnung" drückt,
   * meint neu; ein stehengebliebener Kunde ist die Art Fehler, die erst nach
   * dem Abschliessen auffällt.
   */
  function zuruecksetzen(): void {
    setKundeId('')
    setProjektId(null)
    setProjektName('')
    setProjektSuche('')
    setStrasse('')
    setPlz('')
    setOrt('')
    setVon('')
    setBis('')
    setRabatt(0)
    setBemerkung('')
    setAufgabe('')
    setAufgabeDatum('')
    setAufgabeNotiz('')
    setZeilen([leereZeile()])
    setSuche('')
    setDatum(heute())
    setZielManuell('')
    setBelegId(null)
    setNummer(null)
    setGueltigBis('')
    setHerkunft(null)
    setWahl(null)
    setVorlageName('')
  }

  /** Positionen aus einer fremden Quelle ins Formular übernehmen. */
  function setzeZeilenAus(
    quelle: Array<{
      description: string
      quantity: string | number
      unit: string
      unitPriceRp: number
      vatRate: string | number
      discountPercent: string | number
      kind: 'arbeit' | 'material'
    }>
  ): void {
    if (quelle.length === 0) {
      setZeilen([leereZeile()])
      return
    }
    setZeilen(
      quelle.map((l) => ({
        key: uuidv7(),
        description: l.description,
        quantity: Number(l.quantity),
        unit: l.unit,
        unitPriceRp: Number(l.unitPriceRp),
        vatRate: Number(l.vatRate),
        discountPercent: Number(l.discountPercent),
        kind: l.kind
      }))
    )
  }

  async function wegOeffnen(welcher: 'vorlage' | 'letzte' | 'duplikat'): Promise<void> {
    if (wahl === welcher) {
      setWahl(null)
      return
    }
    setWahl(welcher)
    if (welcher === 'vorlage') {
      const antwort = await api.listTemplates(art)
      setVorlagen(antwort.templates)
    }
    if (welcher === 'duplikat') {
      // Abgeschlossene und gesendete Rechnungen – ein Entwurf taugt nicht als
      // Vorbild, er kann halb fertig sein.
      const a = await api.listDocuments('abgeschlossen')
      const b = await api.listDocuments('gesendet')
      setBelege(
        [...a.documents, ...b.documents]
          // Nur Belege derselben Art: aus einer Rechnung eine Offerte zu
          // duplizieren wäre eine stille Umdeutung, die niemand erwartet.
          .filter((d) => d.number && d.type === art)
          .sort((x, y) => String(y.issueDate).localeCompare(String(x.issueDate)))
          .slice(0, 25)
      )
    }
  }

  /**
   * Vorlage anwenden.
   *
   * Es entsteht kein Beleg. Das Formular wird gefüllt, mehr nicht – und die
   * Vorlage bleibt unberührt, auch wenn danach jede Zeile geändert wird.
   */
  async function vorlageAnwenden(id: string, name: string): Promise<void> {
    const { template, lines } = await api.getTemplate(id)
    if (template.defaultProjectName) setProjektName(template.defaultProjectName)
    if (template.defaultObjectStreet) setStrasse(template.defaultObjectStreet)
    if (template.defaultObjectZip) setPlz(template.defaultObjectZip)
    if (template.defaultObjectCity) setOrt(template.defaultObjectCity)
    if (template.defaultPaymentTermDays != null) setFrist(template.defaultPaymentTermDays)
    if (template.defaultNotes) setBemerkung(template.defaultNotes)
    setRabatt(Number(template.defaultDiscountPercent) || 0)
    setzeZeilenAus(lines)
    // Der Leistungszeitraum bleibt leer. Er gehört zu der Arbeit, die jetzt
    // abgerechnet wird, und nach ihm richtet sich die Mehrwertsteuerperiode.
    setVon('')
    setBis('')
    setHerkunft(`Aus Vorlage „${name}"`)
    setWahl(null)
    notify.ok(`Vorlage „${name}" übernommen.`)
  }

  /**
   * Aus der letzten Rechnung desselben Kunden.
   *
   * „Wie letztes Mal" – der häufigste Satz im Büro. Der Server liefert nur den
   * Formularinhalt; Nummer, PDF, Zahlungen und Versand stehen in seiner Antwort
   * gar nicht drin.
   */
  async function ausLetzterRechnung(): Promise<void> {
    if (!kundeId) throw new Error('Bitte zuerst den Kunden wählen.')
    const antwort = istOfferte ? await api.lastQuoteFor(kundeId) : await api.lastInvoiceFor(kundeId)
    if (!antwort.found || !antwort.head) {
      throw new Error(
        antwort.message ?? `Für diesen Kunden gibt es noch keine abgeschlossene ${wort}.`
      )
    }
    const h = antwort.head
    setProjektName(h.projectName ?? '')
    setStrasse(h.objectStreet ?? '')
    setPlz(h.objectZip ?? '')
    setOrt(h.objectCity ?? '')
    if (h.paymentTermDays != null) setFrist(h.paymentTermDays)
    setRabatt(h.discountPercent || 0)
    setBemerkung(h.notes ?? '')
    setzeZeilenAus(antwort.lines ?? [])
    setVon('')
    setBis('')
    // Der nächste Schritt wird nicht übernommen: „Zahlungseingang prüfen" für
    // eine Rechnung, die es noch nicht gibt, wäre eine Aufgabe, die nie fällig
    // wird und die Arbeitsliste zumüllt.
    setAufgabe('')
    setAufgabeDatum('')
    setAufgabeNotiz('')
    setHerkunft(`Übernommen aus ${wort} ${antwort.source?.number ?? ''}`.trim())
    setWahl(null)
    notify.ok(`Positionen aus ${wort} ${antwort.source?.number ?? ''} übernommen.`)
  }

  /**
   * Rechnung duplizieren.
   *
   * Anders als die beiden anderen Wege schreibt dieser sofort: der Server legt
   * den Entwurf an, weil er beim Kopieren entscheiden muss, was NICHT mitkommt –
   * Nummer, PDF, Zahlungen, Versand und der Rapportbezug der Zeilen. Diese
   * Entscheidung gehört an eine Stelle, und zwar an die, die auch die Sperren
   * kennt.
   */
  async function duplizieren(id: string, nr: string | null): Promise<void> {
    const antwort = await api.duplicateDocument(id, uuidv7())
    const neu = String(antwort.document.id)
    const detail = await api.getDocument(neu)
    const d = detail.document
    setBelegId(neu)
    setNummer(null)
    setKundeId(String(d.customerId))
    setProjektName(d.projectName ?? '')
    setStrasse(d.objectStreet ?? '')
    setPlz(d.objectZip ?? '')
    setOrt(d.objectCity ?? '')
    if (d.paymentTermDays != null) setFrist(d.paymentTermDays)
    setRabatt(Number(d.discountPercent) || 0)
    setBemerkung(d.notes ?? '')
    setzeZeilenAus(detail.lines)
    setVon('')
    setBis('')
    setAufgabe('')
    setAufgabeDatum('')
    setAufgabeNotiz('')
    setHerkunft(`Duplikat von ${wort} ${nr ?? ''}`.trim())
    setWahl(null)
    notify.ok(`Neuer Entwurf aus ${wort} ${nr ?? ''}. Das Original bleibt unverändert.`)
  }

  /**
   * Nach einem Versionskonflikt: den Serverstand holen und anzeigen.
   *
   * Der eine der beiden Wege aus dem Konflikt. Er verwirft die örtlichen
   * Eingaben – deshalb steht er nur hinter einem ausdrücklichen Klick und nie
   * von selbst. Was dabei verloren geht, steht vorher auf dem Bildschirm.
   */
  async function serverstandLaden(): Promise<void> {
    if (!belegId) return
    const detail = await api.getDocument(belegId)
    const d = detail.document
    setKundeId(String(d.customerId))
    setProjektName(d.projectName ?? '')
    setStrasse(d.objectStreet ?? '')
    setPlz(d.objectZip ?? '')
    setOrt(d.objectCity ?? '')
    if (d.paymentTermDays != null) setFrist(d.paymentTermDays)
    setRabatt(Number(d.discountPercent) || 0)
    setBemerkung(d.notes ?? '')
    setzeZeilenAus(detail.lines)
    versionJetzt.current = Number(d.version)
    setBelegVersion(Number(d.version))
    sicherung.entwarnen(false)
    notify.ok('Der Stand vom Server ist geladen. Deine örtlichen Eingaben sind damit ersetzt.')
  }

  /**
   * Nach einem Versionskonflikt: die eigenen Eingaben behalten.
   *
   * Der andere Weg. Er holt die aktuelle Version vom Server und schreibt den
   * Stand aus der Maske darauf – ein Überschreiben, aber ein ausdrückliches.
   * Was der andere geändert hat, steht danach nicht mehr da; genau deshalb
   * wird hier gefragt und nicht stillschweigend gespeichert.
   */
  async function eigenesBehalten(): Promise<void> {
    if (!belegId) return
    const detail = await api.getDocument(belegId)
    versionJetzt.current = Number(detail.document.version)
    setBelegVersion(Number(detail.document.version))
    await speichern(false)
    sicherung.entwarnen(true)
    notify.ok(`Deine Eingaben sind gespeichert. Der vorige Stand der ${wort} ist damit ersetzt.`)
  }

  /** Den aktuellen Formularzustand als Vorlage sichern. */
  async function alsVorlageSichern(): Promise<void> {
    const name = vorlageName.trim()
    if (!name) throw new Error('Bitte der Vorlage einen Namen geben.')
    if (echteZeilen.length === 0) throw new Error('Eine Vorlage ohne Positionen hilft nicht.')
    await api.createTemplate(
      {
        name,
        type: art,
        defaultProjectName: projektName || null,
        defaultObjectStreet: strasse || null,
        defaultObjectZip: plz || null,
        defaultObjectCity: ort || null,
        defaultPaymentTermDays: frist,
        defaultNotes: bemerkung,
        defaultDiscountPercent: rabatt,
        lines: echteZeilen.map((z) => ({
          description: z.description.trim(),
          quantity: z.quantity,
          unit: z.unit,
          unitPriceRp: z.unitPriceRp,
          vatRate: z.vatRate,
          discountPercent: z.discountPercent,
          kind: z.kind
        }))
      },
      uuidv7()
    )
    setVorlageName('')
    notify.ok(`Vorlage „${name}" gespeichert.`)
  }

  /**
   * Speichern: Beleg anlegen, Kopf setzen, Zeilen schicken.
   *
   * Beim zweiten Mal wird nicht neu angelegt – der Beleg bleibt derselbe, und
   * die Zeilen werden ersetzt. Sonst entstünde bei jedem Klick eine Rechnung
   * mehr, und genau so füllt sich eine Belegliste mit Leichen.
   */
  /**
   * Speichern – gereiht, nie zwei gleichzeitig.
   *
   * Alle drei Auslöser (Zeitgeber der Sicherung, «Entwurf speichern»,
   * «Abschliessen») gehen durch diese Tür. Was wartet, läuft danach, und zwar
   * mit dem Formularstand von DANN – `schreibe` liest die Felder beim Start,
   * nicht beim Einreihen. Ein Fehler eines Laufs reisst die Kette nicht ab.
   */
  async function speichern(vollstaendig = true): Promise<string> {
    const dran = kette.current.then(
      () => schreibe(vollstaendig),
      () => schreibe(vollstaendig)
    )
    kette.current = dran.catch(() => undefined)
    return dran
  }

  async function schreibe(vollstaendig = true): Promise<string> {
    /*
      ══════════════════════════════════════════════════════════════════════
      Ein Entwurf darf unvollständig sein – ein Beleg nicht
      ══════════════════════════════════════════════════════════════════════

      Hier standen zwei harte Abbrüche, und sie galten für BEIDE Knöpfe:
      «Entwurf speichern» und «Abschliessen» liefen durch dieselbe Funktion.
      Wer eine halbe Stunde Positionen erfasst und den Kunden noch nicht
      gewählt hatte, konnte nichts sichern – und verlor beim nächsten
      Seitenwechsel alles.

      Jetzt entscheidet der Aufrufer. Der Entwurf speichert, was da ist; das
      Abschliessen verlangt vorher das Nötigste und zeigt es am Feld.

      Die Kundenkennung ist die einzige Ausnahme: Ohne sie kann der Server
      gar keinen Beleg anlegen – die Spalte ist nicht optional. Fehlt sie,
      bleibt der Entwurf im Browser liegen (siehe die örtliche Sicherung) und
      geht trotzdem nicht verloren.
    */
    if (vollstaendig) {
      const fehlt: Record<string, string> = {}
      if (!kundeId) fehlt['ex-kunde'] = 'Bitte wähle einen Kunden aus.'
      if (echteZeilen.length === 0) {
        fehlt['ex-zeilen'] = `Ohne Position gibt es keine ${wort}. Trage mindestens eine Zeile mit Bezeichnung ein.`
      }
      if (Object.keys(fehlt).length > 0) {
        setFehlerAmFeld(fehlt)
        setFehlerOhneFeld([])
        springeZu(Object.keys(fehlt)[0])
        throw new Error(Object.values(fehlt)[0])
      }
    }
    if (!kundeId) {
      setFehlerAmFeld({ 'ex-kunde': 'Bitte wähle einen Kunden aus – ohne ihn lässt sich der Entwurf nicht auf dem Server ablegen.' })
      springeZu('ex-kunde')
      throw new Error('Bitte zuerst einen Kunden wählen.')
    }

    let id = belegId
    const vorhandenerBeleg = Boolean(id)
    if (!id) {
      const antwort = await api.createExpressDocument(
        {
          type: art,
          issueDate: datum || null,
          validUntil: istOfferte ? zielDatum || null : null,
          customerId: kundeId,
          projectId: projektId,
          projectName: projektName || null,
          objectStreet: strasse || null,
          objectZip: plz || null,
          objectCity: ort || null,
          serviceFrom: von || null,
          serviceTo: bis || null,
          paymentTermDays: frist,
          notes: bemerkung || null
        },
        uuidv7()
      )
      id = String(antwort.document.id)
      setBelegId(id)
      /*
        Die Version des frisch angelegten Belegs festhalten.

        Sie wurde hier nicht gesetzt, und damit ging der erste Durchgang nach
        dem Anlegen ohne `basisVersion` hinaus – ungeprüft. Jetzt ist sie von
        der ersten Sekunde an bekannt.
      */
      setBelegVersion(Number(antwort.document.version ?? 1))
      versionJetzt.current = Number(antwort.document.version ?? 1)
    } else {
      const kopf = await api.updateDocument(
        id,
        {
          /*
            Die Basisversion geht MIT, und zwar an den ersten der beiden
            Schreibaufrufe dieses Durchgangs. Das ist die Stelle, an der ein
            zweiter Bearbeiter auffällt – der zweite Aufruf baut danach auf
            der Version auf, die dieser hier bestätigt hat.
          */
          basisVersion: versionJetzt.current ?? undefined,
          /*
            Die Baustelle gehört mit ins Update.

            Sie stand nur im Anlegen. Wer den Entwurf speicherte und danach
            die Baustelle wählte oder wechselte, sah sie im Formular – im
            Beleg blieb die alte oder gar keine. Beim zweiten «Speichern»
            wurde daraus stillschweigend der alte Stand.

            `projectId: null` heisst hier ausdrücklich «keine Baustelle»; der
            Server kennt den Unterschied zwischen «nicht mitgeschickt» und
            «ausdrücklich leer».
          */
          projectId: projektId,
          projectName: projektName || null,
          objectStreet: strasse || null,
          objectZip: plz || null,
          objectCity: ort || null,
          serviceFrom: von || null,
          serviceTo: bis || null,
          paymentTermDays: frist,
          notes: bemerkung || null,
          discountPercent: rabatt,
          issueDate: datum || null,
          validUntil: istOfferte ? zielDatum || null : null
        },
        uuidv7()
      )
      /*
        Die vom Server bestätigte Version übernehmen – SOFORT.

        Hier lag der gemeldete Versionskonflikt. Dieser Durchgang schreibt
        zweimal: erst den Kopf (oben), dann die Positionen (unten, mit
        `basisVersion`). Der Kopfaufruf erhöht die Version am Beleg. Weil er
        sie früher nicht zurückgab, schickte der Zeilenaufruf die Version von
        VOR dem Kopf mit – und der Server antwortete «Dieser Entwurf wurde
        inzwischen an anderer Stelle geändert», obwohl niemand sonst daran
        war. Beim Tippen trat das bei jedem zweiten Sichern auf.

        `versionJetzt` ist eine Referenz und nicht der Zustand: Innerhalb
        desselben Durchgangs ist ein `useState`-Wert noch der alte, und genau
        diese eine Zeile Verzug war der Fehler.
      */
      versionJetzt.current = kopf.version
      setBelegVersion(kopf.version)
      /*
        Die Zeilen in EINEM Zug ersetzen.

        Hier standen vorher zwei Schleifen: jede vorhandene Zeile einzeln
        löschen, jede neue einzeln anlegen. Bei zwölf Positionen waren das
        fünfundzwanzig Serveraufrufe ohne gemeinsame Transaktion – ein
        Abbruch in der Mitte hinterliess einen halb gefüllten Beleg. Für ein
        automatisches Speichern alle paar Sekunden ist das unbrauchbar.
      */
      const antwort = await api.entwurfErsetzen(
        id,
        {
          basisVersion: versionJetzt.current ?? undefined,
          zeilen: echteZeilen.map((z) => ({
            description: z.description.trim(),
            quantity: z.quantity,
            unit: z.unit,
            unitPriceRp: z.unitPriceRp,
            vatRate: z.vatRate,
            discountPercent: z.discountPercent,
            kind: z.kind,
            isExtra: false
          }))
        },
        uuidv7()
      )
      versionJetzt.current = antwort.version
      setBelegVersion(antwort.version)
    }

    if (!vorhandenerBeleg) {
      /*
        Jede angelegte Position erhöht jetzt die Version des Belegs – der
        Server gibt sie zurück, und sie wird hier mitgeführt. Sonst ginge der
        nächste automatische Durchgang mit der Version von VOR den Positionen
        hinaus und meldete einen Konflikt, den es nicht gibt. Dasselbe gilt
        für den Rabatt danach, der die Version schon immer erhöht hat.
      */
      for (const z of echteZeilen) {
        const neu = await api.addDocumentLine(
          id,
          {
            description: z.description.trim(),
            quantity: z.quantity,
            unit: z.unit,
            unitPriceRp: z.unitPriceRp,
            vatRate: z.vatRate,
            discountPercent: z.discountPercent,
            kind: z.kind,
            isExtra: false
          },
          uuidv7()
        )
        if (neu.version) {
          versionJetzt.current = neu.version
          setBelegVersion(neu.version)
        }
      }
      if (rabatt > 0) {
        const nachRabatt = await api.updateDocument(id, { discountPercent: rabatt }, uuidv7())
        if (nachRabatt.version) {
          versionJetzt.current = nachRabatt.version
          setBelegVersion(nachRabatt.version)
        }
      }
    }

    if (aufgabe.trim()) {
      /* Auch die Aufgabe erhöht die Version – sonst wäre der nächste
         Durchgang wieder eine Version im Rückstand. */
      const nachAufgabe = await api.setNextAction(
        id,
        { label: aufgabe.trim(), date: aufgabeDatumWirksam || null, note: aufgabeNotiz || null },
        uuidv7()
      )
      if (nachAufgabe.version) {
        versionJetzt.current = nachAufgabe.version
        setBelegVersion(nachAufgabe.version)
      }
    }

    return id
  }

  /* ═════════════════════════════════════════════════════════════════════════
     Automatisches Speichern
     ═════════════════════════════════════════════════════════════════════════ */

  /*
    Der Stand der Maske als ein Wert.

    Er ist das, was gesichert und was wiederhergestellt wird. Alles darin ist
    Formularinhalt – nichts aus der Sitzung, keine Zugangsdaten, keine
    Kundenliste. Der Benutzer sieht ohnehin schon, was hier steht.
  */
  const stand = useMemo(
    () => ({
      kundeId,
      projektId,
      projektName,
      strasse,
      plz,
      ort,
      von,
      bis,
      rabatt,
      bemerkung,
      datum,
      zielManuell,
      gueltigBis,
      aufgabe,
      aufgabeDatum,
      aufgabeNotiz,
      zeilen
    }),
    [
      kundeId,
      projektId,
      projektName,
      strasse,
      plz,
      ort,
      von,
      bis,
      rabatt,
      bemerkung,
      datum,
      zielManuell,
      gueltigBis,
      aufgabe,
      aufgabeDatum,
      aufgabeNotiz,
      zeilen
    ]
  )

  /*
    Gesichert wird erst, wenn wirklich etwas erfasst ist.

    Ohne diese Bedingung schriebe schon das blosse Öffnen der Maske eine
    Kopie – und beim nächsten Aufruf stünde die Wiederherstellung eines leeren
    Formulars im Angebot. Ein versendeter oder abgeschlossener Beleg wird
    ebenfalls nicht gesichert: Er ist kein Entwurf mehr.
  */
  const sicherungAktiv =
    !versendet && (Boolean(kundeId) || echteZeilen.length > 0 || bemerkung.trim() !== '')

  /*
    Mandant und Person als STABILES Objekt.

    Stand hier bis zur Freigabekorrektur als Objektliteral direkt im Aufruf –
    und damit entstand bei jedem Durchlauf ein neues Objekt. Die Sicherung
    hängt daran; sie hielt jeden Durchlauf für eine Änderung, räumte ihren
    Zeitgeber ab und stellte ihn neu. Zusammen mit einem Serverfehler ergab
    das einen Kreislauf im Anderthalbsekundentakt.
  */
  const werIch = useMemo(
    () => ({ tenantId: session.tenantId, userId: session.userId }),
    [session.tenantId, session.userId]
  )

  const sicherung = useEntwurfsSicherung({
    art,
    stand,
    belegId,
    serverVersion: belegVersion,
    wer: werIch,
    aktiv: sicherungAktiv,
    aufServer: async () => {
      /*
        Ohne Kunden kann der Server keinen Beleg anlegen – die Spalte ist
        nicht optional. Dann bleibt es bei der örtlichen Kopie, und der
        Zustand sagt ehrlich «nicht gespeichert», statt ein Häkchen zu zeigen,
        hinter dem nichts steht.
      */
      if (!kundeId) throw new Error('Ohne Kunden lässt sich der Entwurf nicht ablegen.')
      const id = await speichern(false)
      return id
    },
    aufEinsetzen: (w) => {
      setKundeId(w.kundeId)
      setProjektId(w.projektId)
      setProjektName(w.projektName)
      setStrasse(w.strasse)
      setPlz(w.plz)
      setOrt(w.ort)
      setVon(w.von)
      setBis(w.bis)
      setRabatt(w.rabatt)
      setBemerkung(w.bemerkung)
      setDatum(w.datum)
      setZielManuell(w.zielManuell)
      setGueltigBis(w.gueltigBis)
      setAufgabe(w.aufgabe)
      setAufgabeDatum(w.aufgabeDatum)
      setAufgabeNotiz(w.aufgabeNotiz)
      setZeilen(w.zeilen)
      notify.ok('Der letzte gesicherte Stand ist wieder da.')
    }
  })

  /*
    Ein behobener Fehler verschwindet sofort – nicht erst beim nächsten
    Versuch.

    Beim Nachsehen im Browser stand der rote Rahmen um die Positionsliste
    noch da, nachdem die erste Position erfasst war. Wer eine Vorgabe erfüllt
    hat, soll das auch sehen; ein Rahmen, der bleibt, lässt einen an der
    falschen Stelle weitersuchen.
  */
  useEffect(() => {
    if (echteZeilen.length > 0 && fehlerAmFeld['ex-zeilen']) {
      setFehlerAmFeld((f) => {
        const rest = { ...f }
        delete rest['ex-zeilen']
        return rest
      })
    }
  }, [echteZeilen.length, fehlerAmFeld])

  async function mit<T>(name: string, fn: () => Promise<T>): Promise<T | null> {
    setBusy(name)
    /*
      Ein neuer Versuch beginnt ohne die alten roten Rahmen. Sonst bleibt ein
      Fehler stehen, der gerade behoben wurde, und man sucht an der falschen
      Stelle weiter.
    */
    setFehlerAmFeld({})
    setFehlerOhneFeld([])
    try {
      return await fn()
    } catch (err) {
      /*
        Die Feldliste des Servers auswerten, statt sie zu einem Satz zu
        verschmelzen. Was sich einem Feld zuordnen lässt, bekommt dort den
        roten Rahmen und den Satz; der Rest steht als Band über dem Formular.
        Der Toast bleibt zusätzlich – er ist die Meldung für den Fall, dass
        gerade ein anderer Teil der Seite im Blick ist.
      */
      const befund = ausFehler(err, EXPRESS_FELDER)
      if (Object.keys(befund.amFeld).length > 0 || befund.ohneFeld.length > 0) {
        setFehlerAmFeld(befund.amFeld)
        setFehlerOhneFeld(befund.ohneFeld)
        springeZu(befund.erste)
      }
      notifyError(err)
      return null
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      {pdfAnsicht && (
        <PdfAnsicht
          url={pdfAnsicht.url}
          titel={pdfAnsicht.titel}
          aufZu={() => setPdfAnsicht(null)}
        />
      )}
      <OfficeHead
        titel={nummer ? `${wort} ${nummer}` : `Express-${wort}`}
        unter={
          nummer
            ? 'Abgeschlossen. Die Nummer ist vergeben und der Beleg unveränderlich.'
            : (herkunft ??
              (istOfferte
                ? 'Angebot schreiben. Kunde, Positionen, Gültigkeit, fertig.'
                : 'Rechnung schreiben, ohne Rapport. Kunde, Positionen, fertig.'))
        }
      >
        {/*
          Die vier Wege stehen nebeneinander und nicht in einem Klappmenü.

          In der Vorlage sind sie ebenfalls sichtbar, und aus gutem Grund: wer
          nicht weiss, dass es Vorlagen gibt, sucht auch nicht danach – er tippt
          weiter ab.
        */}
        <button className="o-btn" id="btn-express-neu" onClick={zuruecksetzen}>
          Neue {wort}
        </button>
        {!nummer && (
          <>
            <button
              className={wahl === 'vorlage' ? 'o-btn o-btn-primary' : 'o-btn'}
              id="btn-aus-vorlage"
              onClick={() => void mit('wahl', () => wegOeffnen('vorlage'))}
            >
              Aus Vorlage
            </button>
            <button
              className="o-btn"
              id="btn-aus-letzter"
              onClick={() => void mit('letzte', ausLetzterRechnung)}
            >
              Aus letzter {wort}
            </button>
            <button
              className={wahl === 'duplikat' ? 'o-btn o-btn-primary' : 'o-btn'}
              id="btn-duplizieren"
              onClick={() => void mit('wahl', () => wegOeffnen('duplikat'))}
            >
              Duplizieren
            </button>
          </>
        )}
      </OfficeHead>

      {/* ---------- Auswahl: Vorlage ---------- */}

      {wahl === 'vorlage' && (
        <div className="o-card" id="wahl-vorlage">
          <div className="o-card-head">
            <h3>Vorlage wählen</h3>
            <span className="o-soft">{vorlagen.length} vorhanden</span>
          </div>
          <div className="o-pad">
            {vorlagen.length === 0 ? (
              <p className="o-muted">
                Noch keine Vorlage. Fülle eine Rechnung aus und sichere sie unten unter „Optionen"
                als Vorlage – ab dann steht sie hier.
              </p>
            ) : (
              <div className="o-wahl">
                {vorlagen.map((v) => (
                  <button
                    key={v.id}
                    className="o-wahl-zeile"
                    id={`vorlage-${v.id}`}
                    onClick={() => void mit('vorlage', () => vorlageAnwenden(v.id, v.name))}
                  >
                    <span className="o-wahl-name">{v.name}</span>
                    <span className="o-wahl-meta">
                      {v.lineCount} Positionen · CHF {rp(v.netRp)}
                      {v.description ? ` · ${v.description}` : ''}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------- Auswahl: Rechnung zum Duplizieren ---------- */}

      {wahl === 'duplikat' && (
        <div className="o-card" id="wahl-duplikat">
          <div className="o-card-head">
            <h3>{wort} duplizieren</h3>
            <span className="o-soft">Das Original bleibt unverändert.</span>
          </div>
          <div className="o-pad">
            {belege.length === 0 ? (
              <p className="o-muted">Es gibt noch keine abgeschlossene {wort}.</p>
            ) : (
              <div className="o-wahl">
                {belege.map((d) => (
                  <button
                    key={d.id}
                    className="o-wahl-zeile"
                    id={`dup-${d.id}`}
                    onClick={() => void mit('duplikat', () => duplizieren(d.id, d.number))}
                  >
                    <span className="o-wahl-name">
                      <span className="o-mono">{d.number}</span> {d.customerName}
                    </span>
                    <span className="o-wahl-meta">
                      {d.issueDate} · {d.projectName ?? 'ohne Projekt'} · CHF {rp(d.totalRp)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/*
        Ab hier ist alles Eingabe – und ab der Nummer nichts mehr davon.

        Ein <fieldset disabled> statt vierzig einzelner `disabled`-Angaben:
        der Browser sperrt damit jedes Feld, jede Auswahl und jeden Knopf
        darin, auch die, die morgen dazukommen. Vierzig einzelne Angaben
        wären vierzig Gelegenheiten, eine zu vergessen – und die vergessene
        fällt niemandem auf, weil der Beleg ja meistens ein Entwurf ist.

        Die Summen- und Aktionskarte steht bewusst AUSSERHALB: PDF ansehen,
        senden, Zahlung erfassen und der Weg zum Beleg müssen weiter gehen.
      */}
      {abgeschlossen && (
        <div className="o-band" id="express-abgeschlossen" role="status">
          <span>
            {wort} {nummer} ist versendet und kann nicht mehr bearbeitet werden.
            {istOfferte
              ? ' Für eine Änderung eine neue Offerte erstellen – am schnellsten über «Duplizieren».'
              : ' Für eine Korrektur die Rechnung stornieren und eine neue ausstellen – so bleibt nachvollziehbar, was der Kunde bekommen hat.'}
          </span>
        </div>
      )}

      {/*
        ──────────────────────────────────────────────────────────────────────
        Der liegen gebliebene Stand
        ──────────────────────────────────────────────────────────────────────

        Er wird ANGEBOTEN und nicht eingesetzt. Wer die Maske bewusst neu
        begonnen hat, soll seine Arbeit nicht von einem alten Stand
        überschrieben bekommen – und wer das Telefon verloren hat, findet sie
        mit einem Klick wieder. Nach der Entscheidung verschwindet das Angebot
        in beide Richtungen; zweimal dasselbe wiederherzustellen ist der Weg,
        auf dem doppelte Entwürfe entstehen.
      */}
      {sicherung.gefunden && (
        <div className="o-band warnung" id="ex-wiederherstellen" role="status">
          <span>
            Von dieser Erfassung liegt ein ungesicherter Stand vor –{' '}
            {new Date(sicherung.gefunden.gespeichertAm).toLocaleString('de-CH', {
              dateStyle: 'short',
              timeStyle: 'short'
            })}
            . Er stammt aus diesem Browser und ging nicht an den Server.
          </span>
          <span className="rowactions">
            <button
              type="button"
              className="o-btn"
              id="btn-entwurf-wiederherstellen"
              onClick={sicherung.uebernehmen}
            >
              Wiederherstellen
            </button>
            <button
              type="button"
              className="o-btn-ghost"
              id="btn-entwurf-verwerfen"
              onClick={sicherung.verwerfen}
            >
              Verwerfen
            </button>
          </span>
        </div>
      )}

      {/*
        Der Versionskonflikt – mit zwei Wegen, nicht mit einer Aufforderung.

        Hier stand nichts. Der Server antwortete «Dieser Entwurf wurde
        inzwischen an anderer Stelle geändert. Lade ihn neu, damit nichts
        verloren geht», und das war die ganze Auskunft: ein Neuladen, das
        genau die Eingaben wegwirft, um die es geht.

        Jetzt steht da, was gilt – die Eingaben liegen vollständig in diesem
        Browser – und daneben die beiden Wege, die es wirklich gibt. Beide sind
        ausdrücklich; von selbst überschreibt Raporto nichts, weil auf der
        anderen Seite ein Mensch an demselben Beleg gearbeitet haben kann.
      */}
      {sicherung.zustand === 'konflikt' && (
        <div className="o-band warnung" id="ex-konflikt" role="alert">
          <span>
            Diese {wort} wurde inzwischen an anderer Stelle geändert – auf einem anderen Gerät
            oder in einem zweiten Fenster. Deine Eingaben sind nicht verloren: sie liegen
            vollständig in diesem Browser, nur noch nicht auf dem Server.
          </span>
          <span className="rowactions">
            <ActionButton
              id="btn-konflikt-eigenes"
              variant="primary"
              labels={{
                idle: 'Meine Eingaben speichern',
                busy: 'Speichert …',
                success: 'Gespeichert'
              }}
              onAction={eigenesBehalten}
              onError={(err) => notifyError(err)}
            />
            <ActionButton
              id="btn-konflikt-server"
              labels={{ idle: 'Stand vom Server laden', busy: 'Lädt …', success: 'Geladen' }}
              onAction={serverstandLaden}
              onError={(err) => notifyError(err)}
            />
          </span>
        </div>
      )}

      {/*
        Was sich keinem Feld dieser Maske zuordnen lässt.

        Etwa ein fehlender Konditionstext oder eine fehlende IBAN – beides
        steht in den Einstellungen und nicht hier. Ein roter Rahmen um ein
        Feld, das man an dieser Stelle gar nicht ändern kann, wäre schlimmer
        als keiner; deshalb stehen diese Punkte als Band über dem Formular.
      */}
      {fehlerOhneFeld.length > 0 && (
        <div className="o-fehlerband" id="ex-fehlerband" role="alert">
          <strong>Das fehlt noch, bevor sich der Beleg ausstellen lässt:</strong>
          <ul>
            {fehlerOhneFeld.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        </div>
      )}

      <fieldset className="o-sperrbar" disabled={abgeschlossen}>

      {/* ---------- Kunde und Objekt ---------- */}

      <div className="o-card">
        <div className="o-card-head">
          <h3>Kunde und Objekt</h3>
          {kunde && (
            <button className="o-btn o-btn-ghost" id="btn-adresse-kunde" onClick={adresseVomKunden}>
              Kundenadresse übernehmen
            </button>
          )}
        </div>
        <div className="o-pad">
          <div className="o-grid o-grid-2">
            <div className="o-field">
              <label htmlFor="ex-kunde">Kunde</label>
              <select
                id="ex-kunde"
                value={kundeId}
                onChange={(e) => {
                  setKundeId(e.target.value)
                  /* Ein Projekt gehört genau einem Kunden. Wer den Kunden
                     wechselt, darf die Baustelle des alten nicht behalten –
                     und die angefangene Suche darin auch nicht. */
                  setProjektId(null)
                  setProjektSuche('')
                  /* Der Fehler verschwindet, sobald er behoben ist – nicht
                     erst beim nächsten Versuch. */
                  if (fehlerAmFeld['ex-kunde']) {
                    setFehlerAmFeld((f) => {
                      const { 'ex-kunde': _weg, ...rest } = f
                      return rest
                    })
                  }
                }}
                disabled={versendet}
                aria-invalid={fehlerAmFeld['ex-kunde'] ? true : undefined}
                aria-describedby={fehlerAmFeld['ex-kunde'] ? 'ex-kunde-fehler' : undefined}
              >
                <option value="">– wählen –</option>
                {kunden.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.name}
                  </option>
                ))}
              </select>
              {fehlerAmFeld['ex-kunde'] && (
                <span className="o-feldfehler" id="ex-kunde-fehler" role="alert">
                  {fehlerAmFeld['ex-kunde']}
                </span>
              )}
              {!versendet && (
                <button
                  type="button"
                  id="btn-kunde-neu"
                  className="o-btn-ghost o-neben"
                  onClick={() => setAnlage(anlage === 'kunde' ? null : 'kunde')}
                >
                  Neuer Kunde
                </button>
              )}
              {kunde && (
                <span className="hint">
                  {[kunde.street, [kunde.zip, kunde.city].filter(Boolean).join(' ')]
                    .filter(Boolean)
                    .join(' · ') || 'Keine Adresse hinterlegt'}
                </span>
              )}
            </div>

            {/*
              Projekt / Baustelle – suchen, anhängen, wieder lösen.

              Drei Wege, und alle drei müssen offen bleiben: an eine
              bestehende Baustelle hängen, eine neue daraus anlegen, oder gar
              keine – eine Express-Rechnung für einen Auftrag von zwei
              Stunden braucht kein Projekt.

              Bis hierher gab es nur den zweiten und den dritten. Das Feld war
              ein Textfeld, und jeder Tastendruck darin löschte die
              Verknüpfung. Wer eine Rechnung zu einer laufenden Baustelle
              schreiben wollte, konnte deren Namen abtippen – der Beleg hing
              dann an nichts und stand im Baustellenblatt nirgends.
            */}
            <div className="o-field" id="ex-projektfeld">
              <label htmlFor="ex-projekt">Projekt</label>

              {gewaehlteBaustelle || projektId ? (
                <div className="o-gewaehlt" id="ex-projekt-gewaehlt">
                  <span>
                    <b>{gewaehlteBaustelle?.name ?? projektName}</b>
                    {gewaehlteBaustelle && (
                      <i>
                        {[
                          kunden.find((k) => k.id === gewaehlteBaustelle.customerId)?.name,
                          [gewaehlteBaustelle.street, gewaehlteBaustelle.zip, gewaehlteBaustelle.city]
                            .filter(Boolean)
                            .join(' ')
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </i>
                    )}
                  </span>
                  {!nummer && (
                    <button
                      type="button"
                      id="btn-projekt-loesen"
                      className="o-btn-ghost"
                      onClick={loeseBaustelle}
                    >
                      Entfernen
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <input
                    autoComplete="off"
                    id="ex-projekt"
                    value={projektSuche || projektName}
                    onChange={(e) => {
                      setProjektSuche(e.target.value)
                      /* Was getippt und nicht ausgewählt wird, ist Freitext
                         und steht so auf dem Papier. Das bleibt erlaubt. */
                      setProjektName(e.target.value)
                    }}
                    placeholder="Projekt suchen oder frei benennen"
                    disabled={Boolean(nummer)}
                  />
                  <span className="hint">
                    Ab zwei Zeichen wird gesucht – über Bezeichnung, Adresse, Ort und Kunde.
                    Ohne Auswahl steht der Text nur auf dem Beleg.
                  </span>
                </>
              )}

              {!projektId && projektTreffer.length > 0 && (
                <div className="o-vorschlaege" id="ex-projekt-treffer">
                  {projektTreffer.map(({ projekt, kunde }) => (
                    <button
                      key={projekt.id}
                      type="button"
                      id={`ex-projekt-${projekt.id}`}
                      className="o-vorschlag"
                      onClick={() => waehleBaustelle(projekt)}
                    >
                      <b>{projekt.name}</b>
                      <i>
                        {[
                          kunde?.name,
                          [projekt.street, projekt.zip, projekt.city].filter(Boolean).join(' '),
                          projekt.status && projekt.status !== 'offen' ? projekt.status : null
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </i>
                    </button>
                  ))}
                </div>
              )}

              {!projektId && projektSuche.trim().length >= 2 && projektTreffer.length === 0 && (
                <p className="o-soft" id="ex-projekt-leer" style={{ marginTop: 6 }}>
                  Kein passendes Projekt gefunden.
                  {kundeId ? ' Gesucht wird nur in den Projekten dieses Kunden.' : ''}
                </p>
              )}

              {gewaehlteBaustelle && !nummer && (strasse || plz || ort) && (
                <button
                  type="button"
                  id="btn-projekt-adresse"
                  className="o-btn-ghost o-neben"
                  onClick={() => {
                    setStrasse(gewaehlteBaustelle.street ?? '')
                    setPlz(gewaehlteBaustelle.zip ?? '')
                    setOrt(gewaehlteBaustelle.city ?? '')
                  }}
                >
                  Adresse des Projekts übernehmen
                </button>
              )}

              {!nummer && !projektId && (
                <button
                  type="button"
                  id="btn-projekt-neu"
                  className="o-btn-ghost o-neben"
                  onClick={() => setAnlage(anlage === 'projekt' ? null : 'projekt')}
                >
                  Als Projekt anlegen
                </button>
              )}
            </div>
          </div>

          {anlage === 'kunde' && (
            <KundeAnlegen
              session={session}
              aufEinstellungen={aufEinstellungen}
              aufAbbruch={() => setAnlage(null)}
              aufFertig={(id) => {
                setKundeId(id)
                setProjektId(null)
                setProjektSuche('')
                setAnlage(null)
              }}
            />
          )}

          {anlage === 'projekt' && (
            <ProjektAnlegen
              session={session}
              kundeId={kundeId}
              vorname={projektName}
              adresse={{ street: strasse, zip: plz, city: ort }}
              aufAbbruch={() => setAnlage(null)}
              aufFertig={(id, name) => {
                setProjektId(id)
                setProjektName(name)
                setProjektSuche('')
                setAnlage(null)
              }}
            />
          )}

          <div className="o-section-abstand" />

          <div className="o-grid o-grid-3" style={{ marginTop: 16 }}>
            <div className="o-field">
              <label htmlFor="ex-strasse">Objekt, Strasse</label>
              <StrassenFeld
                id="ex-strasse"
                wert={strasse}
                aufWert={setStrasse}
                aufAdresse={(a) => {
                  setStrasse(a.strasse)
                  if (a.plz) setPlz(a.plz)
                  if (a.ort) setOrt(a.ort)
                }}
                bestand={strassen}
              />
            </div>
            <OrtsFeldPaar
              praefix="ex"
              plz={plz}
              ort={ort}
              aufAenderung={({ plz: p2, ort: o2 }) => {
                setPlz(p2)
                setOrt(o2)
              }}
              ortschaften={orte}
              klassen={{ reihe: null, feld: 'o-field' }}
              hinweis
            />
          </div>

          {/*
            Die Eckdaten stehen sichtbar und nicht unter «Optionen».

            Belegdatum, Fälligkeit und Gültigkeit sind keine Feinheiten – sie
            stehen auf dem Papier und entscheiden, wann gemahnt wird. Vorher
            waren sie zugeklappt, und die Gültigkeit einer Offerte war leer
            mit dem Hinweis, dass schon ein Standard greife. Sichtbar ist
            besser als erklärt.
          */}
          <div className="o-grid o-grid-4" style={{ marginTop: 16 }}>
            <div className="o-field">
              <label htmlFor="ex-datum">{istOfferte ? 'Offertdatum' : 'Rechnungsdatum'}</label>
              <Datumsfeld id="ex-datum" value={datum} onChange={setDatum} />
            </div>
            <div className="o-field">
              <label htmlFor="ex-ziel">{istOfferte ? 'Gültig bis' : 'Zahlbar bis'}</label>
              <Datumsfeld id="ex-ziel" value={zielDatum} onChange={setzeZielDatum} />
              <span className="hint" id="ex-ziel-hinweis">
                {zielSelbstGesetzt
                  ? 'Von Hand gesetzt.'
                  : `Automatisch ${istOfferte ? gueltigTage : frist} Tage nach dem ${
                      istOfferte ? 'Offertdatum' : 'Rechnungsdatum'
                    }.`}
              </span>
            </div>
            {/*
              Die Leistungszeit gehört zur Rechnung, nicht zur Offerte: Bei der
              Offerte ist noch nichts geleistet. Ausgeblendet und nicht
              entfernt – ein Wert aus einem älteren Entwurf bleibt erhalten und
              geht unverändert mit. Die tatsächliche Leistungszeit wird beim
              Weg zur Rechnung erfragt (Quotes.tsx, «Leistungszeitraum»).
            */}
            {!istOfferte && (
              <>
                <div className="o-field">
                  <label htmlFor="ex-von">Leistung von</label>
                  <Datumsfeld id="ex-von" value={von} onChange={setVon} />
                </div>
                <div className="o-field">
                  <label htmlFor="ex-bis">Leistung bis</label>
                  <Datumsfeld id="ex-bis" value={bis} onChange={setBis} />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ---------- Positionen ---------- */}

      <div
        className={fehlerAmFeld['ex-zeilen'] ? 'o-card o-bereich-fehler' : 'o-card'}
        id="ex-zeilen"
      >
        <div className="o-card-head">
          <h3>Positionen</h3>
          <span className="o-soft">{echteZeilen.length} erfasst</span>
        </div>
        {/*
          Der Fehler steht am BEREICH und nicht an einer einzelnen Zeile: Was
          fehlt, ist die Position selbst, und die gibt es noch nicht – es gäbe
          also kein Feld, an das der rote Rahmen gehörte.
        */}
        {fehlerAmFeld['ex-zeilen'] && (
          <div className="o-pad" style={{ paddingBottom: 0 }}>
            <span className="o-feldfehler" id="ex-zeilen-fehler" role="alert">
              {fehlerAmFeld['ex-zeilen']}
            </span>
          </div>
        )}

        <div className="o-pad" style={{ paddingBottom: 8 }}>
          <div className="o-field">
            <label htmlFor="ex-suche">Aus dem Leistungskatalog</label>
            <input
              id="ex-suche"
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              placeholder="Code oder Bezeichnung suchen …"
              autoComplete="off"
            />
          </div>
          {treffer.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {treffer.map((c) => (
                <button
                  key={c.id}
                  className="o-btn"
                  id={`kat-${c.code}`}
                  onClick={() => katalogHinzu(c)}
                >
                  <span className="o-mono">{c.code}</span> {c.name}
                </button>
              ))}
            </div>
          )}
          {/*
            Der Weg aus der leeren Suche.

            Wer etwas sucht, das es nicht gibt, hat zwei vernünftige
            Absichten: er tippt es als Freitext in eine Zeile, oder die
            Leistung fehlt tatsächlich im Katalog. Bisher bot der Bildschirm
            nur das erste an – und deshalb wuchs der Katalog nie, sondern
            dieselbe Position stand in dreissig Rechnungen dreissigmal etwas
            anders geschrieben.
          */}
          {suche.trim().length >= 2 && treffer.length === 0 && !nummer && (
            <p className="o-soft" style={{ marginTop: 8 }}>
              Nichts gefunden.{' '}
              <button
                type="button"
                id="btn-leistung-neu"
                className="o-btn-ghost"
                onClick={() => setAnlage('leistung')}
              >
                «{suche.trim()}» als Leistung anlegen
              </button>
            </p>
          )}
          {anlage === 'leistung' && (
            <LeistungAnlegen
              session={session}
              vorname={suche.trim()}
              mwstVorgabe={mwst}
              vorhandeneCodes={katalog.map((c) => c.code)}
              aufAbbruch={() => setAnlage(null)}
              aufFertig={(eintrag: NeueLeistung) => {
                katalogHinzu(eintrag)
                setAnlage(null)
              }}
            />
          )}
        </div>

        {/*
          Die Tabelle bekommt einen eigenen Kasten.

          Ohne ihn war sie 697 Pixel breit in einer 356 Pixel breiten Karte:
          auf dem Telefon lief die Spalte «Rabatt» rechts aus dem Bild, und
          die Seite selbst wurde seitwärts verschiebbar. Der Kasten trägt die
          Breite; auf schmalen Geräten stapelt die Regel in office.css die
          Zeilen ohnehin untereinander.
        */}
        <div className="o-tabelle o-positionsliste">
        <table className="o-data">
          <thead>
            <tr>
              <th style={{ width: '38%' }}>Bezeichnung</th>
              <th className="o-num" style={{ width: 90 }}>
                Menge
              </th>
              <th style={{ width: 90 }}>Einheit</th>
              <th className="o-num" style={{ width: 110 }}>
                Preis
              </th>
              <th className="o-num" style={{ width: 80 }}>
                Rabatt %
              </th>
              <th className="o-num" style={{ width: 110 }}>
                Betrag
              </th>
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => {
              /*
                Dieselbe Rechnung wie in der Summenbox – und wie auf dem Server.

                Hier stand eine eigene Formel: Menge × Preis × (1 − Rabatt),
                in einem Schritt gerundet. `calcBeleg` rechnet zweistufig –
                erst die Zeilensumme runden, dann den Rabatt davon abziehen.
                Bei einem Positionsrabatt laufen die beiden um einen Rappen
                auseinander, und dann widerspricht die Betragsspalte der
                Summe darunter. Zwei Zahlen für dieselbe Zeile, auf demselben
                Bildschirm.
              */
              const roh = lineTotal(z.quantity, z.unitPriceRp)
              const betrag = roh - (z.discountPercent ? percentOf(roh, z.discountPercent) : 0)
              return (
                <tr key={z.key}>
                  <td data-feld="Bezeichnung">
                    <input
                      autoComplete="off"
                      value={z.description}
                      placeholder="Freitext oder aus dem Katalog"
                      onChange={(e) => setzeZeile(z.key, { description: e.target.value })}
                    />
                  </td>
                  <td className="o-num" data-feld="Menge">
                    <Zahlenfeld
                      aria-label="Menge"
                      nachkomma={2}
                      placeholder="0"
                      wert={z.quantity}
                      aufWert={(n) => setzeZeile(z.key, { quantity: n })}
                    />
                  </td>
                  <td data-feld="Einheit">
                    <input
                      autoComplete="off"
                      value={z.unit}
                      placeholder="m², Std., Psch."
                      onChange={(e) => setzeZeile(z.key, { unit: e.target.value })}
                    />
                  </td>
                  <td className="o-num" data-feld="Preis">
                    <Betragsfeld
                      aria-label="Preis"
                      rappen={z.unitPriceRp}
                      aufRappen={(n) => setzeZeile(z.key, { unitPriceRp: n })}
                    />
                  </td>
                  <td className="o-num" data-feld="Rabatt %">
                    <Zahlenfeld
                      aria-label="Rabatt in Prozent"
                      nachkomma={1}
                      placeholder="0"
                      wert={z.discountPercent}
                      aufWert={(n) => setzeZeile(z.key, { discountPercent: n })}
                    />
                  </td>
                  <td className="o-num o-betrag" data-feld="Betrag">{rp(betrag)}</td>
                  <td>
                    <button
                      className="o-btn o-btn-ghost"
                      aria-label="Zeile entfernen"
                      onClick={() =>
                        setZeilen((alt) =>
                          alt.length === 1 ? [leereZeile()] : alt.filter((x) => x.key !== z.key)
                        )
                      }
                    >
                      ×
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        </div>

        <div className="o-pad">
          <button
            className="o-btn o-btn-ghost"
            id="btn-zeile-hinzu"
            onClick={() => setZeilen((alt) => [...alt, leereZeile()])}
          >
            + Position hinzufügen
          </button>
        </div>
      </div>

      {/* ---------- Optionen ---------- */}

      <div className="o-card">
        <div className="o-card-head">
          <h3>Optionen</h3>
          {/*
            Nach dem Abschluss bleibt die Klappe zu.

            Der Knopf steht im gesperrten Bereich und ist damit ohnehin nicht
            zu bedienen; ein Knopf, der nichts tut, ist schlechter als keiner.
            Was in den Optionen steht – Leistungszeitraum, Bemerkung,
            Zahlungsfrist –, steht auf dem Beleg selbst, und der lässt sich
            mit «PDF ansehen» lesen.
          */}
          {!abgeschlossen && (
            <button
              className="o-btn o-btn-ghost"
              id="btn-optionen"
              onClick={() => setOptionenOffen((v) => !v)}
            >
              {optionenOffen ? 'Zuklappen' : 'Aufklappen'}
            </button>
          )}
        </div>
        {optionenOffen && !abgeschlossen && (
          <div className="o-pad">
            <div className="o-grid o-grid-3">
              <div className="o-field">
                <label htmlFor="ex-frist">
                  {istOfferte ? 'Gültigkeit (Tage)' : 'Zahlungsfrist (Tage)'}
                </label>
                <Zahlenfeld
                  id="ex-frist"
                  wert={istOfferte ? gueltigTage : frist}
                  aufWert={(tage) => {
                    /* Die Tage ändern das Zieldatum oben – solange es niemand
                       selbst gesetzt hat. Sonst bliebe hier eine Zahl stehen,
                       die zu dem Datum daneben nicht passt. */
                    setZielManuell('')
                    if (istOfferte) {
                      setGueltigTage(tage)
                      setGueltigBis('')
                    } else {
                      setFrist(tage)
                    }
                  }}
                />
                <span className="hint">
                  Gilt nur für {istOfferte ? 'diese Offerte' : 'diese Rechnung'}. Das Datum oben
                  wandert mit.
                </span>
              </div>
              <div className="o-field">
                <label htmlFor="ex-rabatt">Gesamtrabatt (%)</label>
                <Zahlenfeld
                  id="ex-rabatt"
                  nachkomma={1}
                  placeholder="0"
                  wert={rabatt}
                  aufWert={setRabatt}
                />
                <span className="hint">Zusätzlich zum Zeilenrabatt.</span>
              </div>
              <div className="o-field">
                <label htmlFor="ex-bemerkung">
                  Bemerkung auf {istOfferte ? 'der Offerte' : 'der Rechnung'}
                </label>
                <input
                  autoComplete="off"
                  id="ex-bemerkung"
                  value={bemerkung}
                  onChange={(e) => setBemerkung(e.target.value)}
                />
              </div>
            </div>

            <div className="office-section" style={{ marginTop: 26 }}>
              Nächster Schritt
            </div>
            <div className="o-grid o-grid-3">
              <div className="o-field">
                <label htmlFor="ex-aufgabe">Aufgabe</label>
                <input
                  autoComplete="off"
                  id="ex-aufgabe"
                  value={aufgabe}
                  onChange={(e) => setAufgabe(e.target.value)}
                  placeholder={istOfferte ? 'z.B. Offerte nachfassen' : 'z.B. Zahlungseingang prüfen'}
                />
              </div>
              <div className="o-field">
                <label htmlFor="ex-aufgabe-datum">Fällig am</label>
                <Datumsfeld id="ex-aufgabe-datum" value={aufgabeDatumWirksam} onChange={setAufgabeDatum} />
                {istOfferte && !aufgabeDatum && nachfassVorschlag && (
                  <span className="hint" id="ex-aufgabe-hinweis">
                    Vorschlag: {Math.ceil(gueltigkeitTage / 2)} Tage nach dem Offertdatum, zur Hälfte
                    der Gültigkeit.
                  </span>
                )}
              </div>
              <div className="o-field">
                <label htmlFor="ex-aufgabe-notiz">Notiz</label>
                <input
                  autoComplete="off"
                  id="ex-aufgabe-notiz"
                  value={aufgabeNotiz}
                  onChange={(e) => setAufgabeNotiz(e.target.value)}
                />
              </div>
            </div>
            <p className="o-soft" style={{ marginTop: 10 }}>
              Was hier steht, erscheint später im Dashboard unter „Heute zu erledigen".
            </p>

            {/*
              Vorlage sichern steht hier und nicht oben bei den Startwegen: man
              weiss erst am Ende, ob sich das Ausgefüllte lohnt aufzuheben.
            */}
            <div className="office-section" style={{ marginTop: 26 }}>
              Als Vorlage sichern
            </div>
            <div className="o-grid o-grid-3">
              <div className="o-field">
                <label htmlFor="ex-vorlagename">Name der Vorlage</label>
                <input
                  autoComplete="off"
                  id="ex-vorlagename"
                  value={vorlageName}
                  onChange={(e) => setVorlageName(e.target.value)}
                  placeholder="z.B. Parkett schleifen und versiegeln"
                />
              </div>
              <div className="o-field" style={{ justifyContent: 'flex-end' }}>
                <button
                  className="o-btn"
                  id="btn-vorlage-sichern"
                  disabled={busy !== null || !vorlageName.trim()}
                  onClick={() => void mit('vorlage-sichern', alsVorlageSichern)}
                >
                  {busy === 'vorlage-sichern' ? 'Sichert …' : 'Als Vorlage speichern'}
                </button>
              </div>
              <div className="o-field">
                <span className="hint" style={{ marginTop: 22 }}>
                  Sichert Positionen, Projekt, Objektadresse, Zahlungsfrist und Rabatt. Nicht den
                  Kunden und nicht den Leistungszeitraum.
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      </fieldset>

      {/* ---------- Summe und Aktionen ---------- */}

      <div className="o-card">
        <div className="o-pad">
          <div className="o-summe">
            <div className="o-summe-zeile">
              <span className="o-muted">Zwischensumme</span>
              <span>{rp(summe.brutto)}</span>
            </div>
            {summe.rabattRp > 0 && (
              <div className="o-summe-zeile">
                <span className="o-muted">Rabatt {rabatt} %</span>
                <span>− {rp(summe.rabattRp)}</span>
              </div>
            )}
            {/*
              Der Satz steht auch dann da, wenn noch keine Position ihn trägt.

              Vorher stand hier «MWST 0.00» ohne Satz, solange der Beleg leer
              war – und genauso bei einem Betrieb, der gar nicht
              mehrwertsteuerpflichtig ist. Zweimal dieselbe Zeile für zwei
              verschiedene Sachverhalte: «noch nichts erfasst» und «wir
              verrechnen ohne MWST». Wer den Satz sucht, fand ihn nirgends.

              Jetzt sagt die Zeile, was gilt. Anzeigen, nicht bearbeiten: der
              Satz gehört dem Betrieb und steht in den Einstellungen; der
              Server rechnet ohnehin selbst nach.
            */}
            {summe.saetze.length === 0 ? (
              <div className="o-summe-zeile">
                <span className="o-muted" id="ex-mwst-satz">
                  {mwst > 0 ? `MWST ${satzText(mwst)} %` : 'Keine MWST'}
                </span>
                <span>{mwst > 0 ? rp(0) : '–'}</span>
              </div>
            ) : (
              summe.saetze.map((s) => (
                <div className="o-summe-zeile" key={s.satz}>
                  <span className="o-muted" id="ex-mwst-satz">
                    MWST {satzText(s.satz)} %
                  </span>
                  <span>{rp(s.betragRp)}</span>
                </div>
              ))
            )}
            {/*
              Die Rundungszeile – nur wenn wirklich gerundet wurde.

              Es gibt keine Ein- und Zwei-Rappen-Stücke mehr; ein Endtotal von
              CHF 100.03 lässt sich nicht bezahlen. Die Differenz steht offen
              da und wird nicht in die Mehrwertsteuer hineingerechnet, sonst
              stimmt die Grundlage nicht mehr zu ihrem eigenen Satz.
            */}
            {summe.rundungRp !== 0 && (
              <div className="o-summe-zeile">
                <span className="o-muted" id="ex-rundung-text">
                  Rundung auf 5 Rappen
                </span>
                <span id="ex-rundung">
                  {summe.rundungRp < 0 ? '−' : '+'}
                  {rp(Math.abs(summe.rundungRp))}
                </span>
              </div>
            )}
            <div className="o-summe-zeile total">
              <span>Total CHF</span>
              <span id="ex-total">{rp(summe.total)}</span>
            </div>
          </div>
        </div>

        {/*
          Die Bestätigung steht AUF dem Bildschirm, nicht nur im Hinweisfeld.

          Nach dem Abschliessen meldete nur `notify.ok` den Erfolg – ein
          Streifen, der nach ein paar Sekunden verschwindet. Auf dem Telefon
          liegt er zudem über der Tastatur oder ausserhalb des sichtbaren
          Bereichs, und wer ihn verpasst, weiss nicht, ob die Offerte
          entstanden ist; gemessen auf 390 px war das der häufigste Zweifel.

          Hier steht sie fest: Art, Nummer, Endbetrag und – darunter, in der
          Aktionsleiste – der Weg in den Beleg. Der Endbetrag gehört dazu, weil
          er die Zahl ist, die gespeichert wurde: gerundet auf fünf Rappen,
          dieselbe, die im PDF steht.
        */}
        {nummer && (
          <div className="o-pad" style={{ borderTop: '1px solid var(--border)' }}>
            <p className="o-band erfolg" id="express-fertig" role="status">
              {wort} {nummer} ist gespeichert – Total CHF {rp(summe.total)}
              {pdfDa ? ', PDF erzeugt' : ''}.
            </p>
          </div>
        )}

        <div className="o-pad" style={{ borderTop: '1px solid var(--border)' }}>
          <div className="office-actions">
            {!nummer && (
              <>
                <button
                  className="o-btn"
                  id="btn-entwurf-speichern"
                  disabled={busy !== null}
                  onClick={() =>
                    void mit('entwurf', async () => {
                      const id = await speichern(false)
                      notify.ok('Entwurf gespeichert.')
                      return id
                    })
                  }
                >
                  {busy === 'entwurf' ? 'Speichert …' : 'Entwurf speichern'}
                </button>

                {/*
                  Der Speicherstand – und keine falsche Erfolgsmeldung.

                  «Gespeichert» steht erst da, wenn der Server geantwortet hat.
                  Schlägt es fehl, steht «Nicht gespeichert» und die örtliche
                  Kopie bleibt liegen. Ein Häkchen, das nur bedeutet «wir haben
                  es versucht», ist schlimmer als gar keine Anzeige.

                  Ohne Kunden kann der Server den Beleg gar nicht anlegen –
                  `documents.customer_id` verlangt einen. Das ist kein Fehler,
                  sondern ein fehlender Schritt, und der Satz sagt deshalb
                  etwas anderes als bei einem Netzfehler: nicht «ging schief»,
                  sondern «wähle den Kunden, dann liegt der Entwurf oben».
                  Verloren geht in der Zwischenzeit nichts – die örtliche
                  Kopie übersteht auch das Abmelden, weil es sie sonst
                  nirgends gibt.
                */}
                {sicherungAktiv && sicherung.zustand !== 'ruht' && (
                  <span
                    className={
                      sicherung.zustand === 'ungesichert' || sicherung.zustand === 'konflikt'
                        ? 'o-warn-text'
                        : 'o-soft'
                    }
                    id="ex-sicherungsstand"
                    role="status"
                    style={{ alignSelf: 'center' }}
                  >
                    {sicherung.zustand === 'speichert'
                      ? 'Speichert …'
                      : sicherung.zustand === 'gespeichert'
                        ? 'Gespeichert'
                        : sicherung.zustand === 'konflikt'
                          ? 'Nicht gespeichert – dieser Beleg wurde woanders geändert.'
                          : kundeId
                            ? 'Nicht gespeichert – der Stand liegt in diesem Browser.'
                            : 'Nur auf diesem Gerät gesichert. Wähle den Kunden, dann liegt der Entwurf auch auf dem Server.'}
                  </span>
                )}

                <button
                  className="o-btn o-btn-primary"
                  id="btn-finalisieren"
                  disabled={busy !== null}
                  onClick={() =>
                    void mit('final', async () => {
                      const id = await speichern()
                      const antwort = await api.finalizeDocument(id, uuidv7())
                      setNummer(antwort.number ?? null)
                      setPdfDa(antwort.pdfReady === true)
                      if (antwort.pdfReady) {
                        notify.ok(`${wort} ${antwort.number} ist bereit – mit PDF.`)
                      } else {
                        notify.error(
                          `${wort} ${antwort.number} hat die Nummer bekommen, aber das PDF ` +
                            `liess sich nicht drucken. ${antwort.error ?? ''} ` +
                            `Im Beleg lässt es sich erneut erzeugen.`
                        )
                      }
                      return antwort
                    })
                  }
                >
                  {busy === 'final' ? 'Schliesst ab …' : 'Abschliessen und PDF'}
                </button>
              </>
            )}

            {nummer && belegId && (
              <>
                {/*
                  Die Aktionsleiste nach dem Abschluss.

                  Hier standen «PDF öffnen» und sonst nichts. Der Beleg war
                  fertig, und der Bildschirm sagte nicht, was als Nächstes
                  dran ist – senden, bezahlen, ansehen. Wer weiterwollte,
                  ging über Menü und Liste zurück zum selben Beleg: gemessen
                  zwei Klicks und ein Ortswechsel, bei jedem Beleg.

                  Die Reihenfolge ist die der Arbeit: ansehen, senden,
                  kassieren, im Beleg weiterarbeiten. Die hervorgehobene
                  Aktion ist die, die als Nächstes dran ist – senden, solange
                  nichts hinausgegangen ist.
                */}
                {pdfDa && (
                  <button
                    type="button"
                    className="o-btn"
                    id="btn-pdf-oeffnen"
                    onClick={() =>
                      setPdfAnsicht({ url: `/documents/${belegId}/pdf`, titel: nummer })
                    }
                  >
                    PDF ansehen
                  </button>
                )}

                {/*
                  Herunterladen steht daneben, nicht in der PDF-Ansicht.

                  Wer den Beleg dem Kunden über einen anderen Weg schickt –
                  WhatsApp, Drucker, eigenes Mailprogramm –, braucht die
                  Datei und nicht die Ansicht. Ein Griff dafür gehört dorthin,
                  wo der Beleg fertig wird.
                */}
                {pdfDa && (
                  <a
                    className="o-btn"
                    id="btn-pdf-laden-express"
                    href={`/documents/${belegId}/pdf`}
                    download={`${nummer}.pdf`}
                  >
                    Herunterladen
                  </a>
                )}

                {wege?.kundenversand.moeglich && (
                  <ActionButton
                    id="btn-express-versand-kunde"
                    variant="primary"
                    actionClass="B"
                    labels={{
                      idle: 'An Kunden senden',
                      busy: 'Öffnet …',
                      success: 'Bereit'
                    }}
                    onAction={async () => {
                      const v = await api.mailvorschlag(belegId, 'kunde')
                      setMail({ weg: 'kunde', empfaenger: v.empfaenger, betreff: v.betreff, text: v.text })
                    }}
                    onError={(err) => notifyError(err)}
                  />
                )}

                {wege && !wege.kundenversand.moeglich && (
                  <ActionButton
                    id="btn-express-versand-selbst"
                    actionClass="B"
                    labels={{
                      idle: 'An eigene Adresse senden',
                      busy: 'Öffnet …',
                      success: 'Bereit'
                    }}
                    onAction={async () => {
                      const v = await api.mailvorschlag(belegId, 'benutzer')
                      setMail({ weg: 'benutzer', empfaenger: v.empfaenger, betreff: v.betreff, text: v.text })
                    }}
                    onError={(err) => notifyError(err)}
                  />
                )}

                {/*
                  Ausgedruckt und in die Post – der häufigste Weg, von dem
                  Raporto sonst nichts erfährt. Ohne den Vermerk wird diese
                  Rechnung nie überfällig, und der Betrieb mahnt nie.
                */}
                {wege?.vermerk?.moeglich && (
                  <ActionButton
                    id="btn-express-als-versendet"
                    actionClass="B"
                    labels={{
                      idle: 'Als versendet vermerken',
                      busy: 'Speichert …',
                      success: 'Vermerkt'
                    }}
                    onAction={async () => {
                      await api.alsVersendetVermerken(belegId, uuidv7())
                      setWege(await api.versandwege(belegId))
                      notify.ok(
                        `${wort} ${nummer} ist als versendet vermerkt. Raporto hat nichts gesendet.`
                      )
                    }}
                    onError={(err) => notifyError(err)}
                  />
                )}

                {!istOfferte && aufBeleg && (
                  <button
                    className="o-btn"
                    id="btn-express-zahlung"
                    onClick={() => aufBeleg(belegId)}
                  >
                    Zahlung erfassen
                  </button>
                )}

                {aufBeleg && (
                  <button className="o-btn" id="btn-zum-beleg" onClick={() => aufBeleg(belegId)}>
                    {istOfferte ? 'Zur Offerte' : 'Zur Rechnung'}
                  </button>
                )}
              </>
            )}
          </div>

          {/*
            Warum der Direktversand nicht geht – als Satz, nicht als grauer Knopf.

            Ein gesperrter Knopf ohne Begründung ist die unfreundlichste Art,
            Nein zu sagen: Man sieht, dass etwas möglich wäre, und erfährt
            nicht, woran es liegt. Der Grund kommt vom Server
            (`versandwege.kundenversand.grund`); steht dort nichts, sagt der
            Satz wenigstens, was einzurichten ist. Daneben steht, was
            stattdessen geht – ansehen, herunterladen, an die eigene Adresse.
          */}
          {nummer && wege && !wege.kundenversand.moeglich && (
            <p className="hint" id="express-versand-grund" style={{ marginTop: 10 }}>
              {wege.kundenversand.grund ??
                'Direktversand an Kunden benötigt eingerichteten E-Mail-Versand.'}{' '}
              Bis dahin: PDF ansehen, herunterladen oder an die eigene Adresse senden.
            </p>
          )}

          {wege?.vermerk?.am && (
            <p className="hint" id="express-vermerk" style={{ marginTop: 10 }}>
              Als versendet vermerkt am {new Date(wege.vermerk.am).toLocaleString('de-CH')} – ohne
              E-Mail. Zurücknehmen lässt sich das im Beleg.
            </p>
          )}

          {nummer && !pdfDa && (
            <p className="o-band warnung" id="pdf-fehlt-hinweis" style={{ marginTop: 12 }}>
              {wort} {nummer} hat die Nummer, aber noch kein PDF. Der Beleg ist gültig und die
              Nummer vergeben – das PDF lässt sich im Beleg unter «PDF erneut erzeugen» nachholen.
            </p>
          )}

          {!nummer && (
            <p className="o-soft" style={{ marginTop: 12 }}>
              {istOfferte
                ? 'Beim Abschliessen vergibt der Server die Offertnummer und erzeugt das PDF – ' +
                  'ohne Zahlteil, denn eine Offerte ist kein Zahlungsbeleg. Ändern lässt sie ' +
                  'sich danach weiterhin, solange sie nicht versendet ist; die Nummer bleibt ' +
                  'dabei dieselbe und das PDF wird neu erzeugt.'
                : 'Beim Abschliessen vergibt der Server die Nummer und erzeugt PDF und Zahlteil. ' +
                  'Ändern lässt sich die Rechnung danach weiterhin, solange sie nicht versendet ' +
                  'ist; die Nummer bleibt dabei dieselbe und das PDF wird neu erzeugt. Ab dem ' +
                  'Versand hilft nur noch stornieren.'}
            </p>
          )}
        </div>
      </div>

      {/*
        Der E-Mail-Editor – dieselbe Komponente wie im Belegdetail, damit es
        nicht zwei Versanddialoge gibt, die sich nach der ersten Änderung
        unterscheiden.
      */}
      {mail && belegId && (
        <MailEditor
          entwurf={mail}
          titel={
            mail.weg === 'kunde'
              ? `${wort} an den Kunden senden`
              : 'PDF an die eigene Adresse senden'
          }
          anhang={`${nummer ?? 'Beleg'}.pdf`}
          aufAendern={setMail}
          aufSenden={async () => {
            const r = await api.versenden(belegId, mail.weg, uuidv7(), {
              betreff: mail.betreff,
              text: mail.text
            })
            setWege(await api.versandwege(belegId))
            notify.ok(
              mail.weg === 'kunde'
                ? `${wort} ${nummer} ist an ${r.empfaenger} gegangen.`
                : `${wort} ${nummer} liegt in deinem Postfach.`
            )
            /* Erst nach dem Erfolg schliessen – sonst ist der Text weg. */
            setMail(null)
          }}
          aufAbbrechen={() => setMail(null)}
        />
      )}
    </>
  )
}
